import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Subject, Subscription } from 'rxjs';
import {
  Component,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { uniqBy, flatMap } from 'lodash';
import { ConfirmationService, LazyLoadEvent, MessageService, SelectItem } from 'primeng';
import { Message } from '../../classes/Message';
import { IAccountCode } from '../../interfaces/account-code';
import { ICustomer } from '../../interfaces/customer';
import { IDepartment } from '../../interfaces/department';
import { IDepot } from '../../interfaces/depot';
import { IDisputeFilter } from '../../interfaces/dispute-filter';
import { IDriver } from '../../interfaces/driver';
import { IFuelReading } from '../../interfaces/fuel-reading';
import { ICancelData, IJobScreen, IOrderScreen } from '../../interfaces/job/job';
import { IOdometerReading } from '../../interfaces/odometer-reading';
import { IOrderTag } from '../../interfaces/order-tag';
import { IPageResult } from '../../interfaces/page-result';
import { IPauseTask } from '../../interfaces/pause-task';
import { IRegion } from '../../interfaces/region';
import {
  IDriverMessage,
  IPhotoOrganisationTaskStep,
  ITaskInfo,
  ITaskNote,
  ITaskOrganisationSpecificSteps,
  ITaskStep,
  ITaskStepNode,
  ITaskStepPhotoInfo,
  IVehicle,
} from '../../interfaces/task-info';
import { ITeam } from '../../interfaces/team';
import { ITrucks } from '../../interfaces/trucks';
import { AccountCodeService } from '../../services/account-code.service';
import { AdditionalChargeService } from '../../services/additional-charge.service';
import { ChecklistService } from '../../services/checklist.service';
import { DisputeService } from '../../services/dispute.service';
import { IDriverService } from '../../services/driver.service';
import { EngineiusRegionService } from '../../services/engineius-region.service';
import { FileStorageService } from '../../services/file-storage.service';
import { InboxBackService } from '../../services/inbox-back.service';
import { IncidentsService } from '../../services/incidents.service';
import { InsuranceProvidersService } from '../../services/insurance-providers.service';
import { IRemoveSupplier, JobsService } from '../../services/job.service';
import { LoggerService } from '../../services/logger.service';
import { OrderTagService } from '../../services/order-tag.service';
import { OrderService } from '../../services/order.service';
import { OrganisationSpecificStepsService } from '../../services/organisation-specific-steps.service';
import { OrganisationsService } from '../../services/organisations.service';
import { SettingsService } from '../../services/settings.service';
import { SupplierService } from '../../services/supplier.service';
import { TaskService } from '../../services/task.service';
import { TrucksService } from '../../services/trucks.service';
import { UserService } from '../../services/user.service';
import { VehicleService } from '../../services/vehicle.service';
import { floorDate } from '../../utils/floor-date';
import { InputHelper } from '../../utils/input.helper';
import { NewDisputeComponent } from '../dispute/new-dispute.component';
import { MarkerPoint } from '../map-control/marker.point';
import { IGalleryImage } from './job-screen-gallery.component';
import { ISystemSettings } from '../../interfaces/system-settings';
import { StepType } from './step.type';
import { OrganisationTypeEnum } from '../../enums/organisation-types';
import {
  AgreementService,
  EventService,
  JobsDeploymentService,
  JobUnassignedReasonService,
  PhotoAreaService,
  TaggedLogger,
  TelephonyService,
  VmeInboxJobsService,
} from '../../services';
import { ITelephonyEvent, TelephonyEventEnum } from '../../interfaces/telephony/telephony-event';
import { IUploadResultDTO } from 'src/app/interfaces/upload-result';
import { IJobAssignment } from '../../interfaces/job-assignment';
import { ITaskStepAnswer } from '../../interfaces/answer';
import { ISyncOrganisationStepDTO } from '../../interfaces/sync-organisation-step';
import { ITaskDelayFailure } from '../../interfaces/delay-failure/task-delay-failure';
import { OrganisationSelectorEventTypeEnum } from '../../interfaces/telephony/organisation-selector-event-type.enum';
import { IOrganisationSelectorEvent } from '../../interfaces/telephony/organisation-selector-event';
import { RefreshJobEventTypeEnum } from '../../enums/refresh-job-event-type.enum';
import { ISupplierCard } from '../../interfaces/supplier-card.d';
import { RegStatusEnum } from '../../enums/reg-status.enum';
import { AdditionalChargeStatusEnum } from '../../enums/additional-charge.enum';
import { AdditionalChargeTypeEnum } from '../../enums/additional-charge-type.enum';
import { IPricePremium, IPricePremiumSummary } from '../../interfaces/price-premium';
import moment from 'moment';
import { INgxGalleryImage } from 'src/app/user-controls/ngx-gallery/ngx-gallery-image.model';
import { BannerDisplayWeekEnum } from '../../enums/banner-management/banner-display-week.enum';
import {
  ITaskSiteOpeningHoursDisplay,
  SiteOpeningHoursService,
} from '../../services/site-opening-hours.service';
import { ICustomerAdditionalCharge } from 'src/app/interfaces/additional-charge';
import { IUser } from 'src/app/interfaces/user';
import {
  ICancellationStatus,
  ISecondaryCancellationStatus,
} from 'src/app/interfaces/cancellation-status';
import { ISaveRegStatus } from '../../interfaces/save-reg-status';
import { ILogCallJobInfo, ILogCallTaskInfo } from '../../interfaces/telephony/log-call-job-info';
import { IPhotoArea } from '../../interfaces/photo-area';
import { TaskType } from '../../enums/task.type';
import {
  JobScreenInspectionHandover,
  JobScreenInspectionHandoverMedia,
} from '../../interfaces/screen-specific/job-screen/common/job-screen-inspection-handover';
import { TaskSupplierScheduleSource } from '../task-supplier-schedule/task-supplier-schedule-store';
import { JobAnalyticsService } from '../../services/product-analytics/job-analytics.service';
import { FuelLevelService } from 'src/app/services/fuel-level.service';

export enum OrderStatus {
  Completed = 1,
  Invoiced = 2,
  Closed = 3,
  Cancelled = 4,
  Created = 5,
  Started = 6,
}

export enum JobStatus {
  Completed = 1,
  Invoiced = 2,
  Closed = 3,
  Cancelled = 4,
  Created = 5,
  Started = 6,
  Paused = 7,
}

export enum PhotoArea {
  Front = 0,
  Offside = 1,
  Rear = 2,
  Nearside = 3,
  Top = 4,
  FuelGaugeOdometer = 5,
  Additional = 6,
}

export enum PhotoAreaCondition {
  Good = 0,
  MinorDamage = 1,
  MajorDamage = 2,
  Uninspected = 3,
  NotRequired = 4,
}

export enum CancellationStatus {
  EngineiusAgreedCancellation = 1,
  CustomerAgreedCancellation = 2,
  EngineiusCancellation = 3,
  CustomerCancellation = 4,
  SupplierCancellation = 5,
  EngineiusError = 6,
  CustomerError = 7,
  SupplierError = 8,
}

@Component({
  selector: 'job-screen',
  styleUrls: ['./job-screen.component.scss'],
  templateUrl: './job-screen.component.html',
  encapsulation: ViewEncapsulation.None,
  providers: [InputHelper, ConfirmationService],
})
export class JobScreenComponent implements OnInit, OnDestroy {
  public isBusy: boolean = false;
  public isShowChangeSupplierDetails: boolean = false;
  public isShowChangeSupplierPriceAccount: boolean = false;
  public isShowChangeDriverTruck: boolean = false;
  public isShowChangeJobDetails: boolean = false;
  public isShowAddDriverNotes: boolean = false;
  public isShowEditOrder: boolean = false;
  public isShowAddPhotoToTaskStep: boolean = false;
  public isShowEditPhotoToTaskStep: boolean = false;
  public showOdometerImage: boolean = false;
  public organisationTypeId: number = 0;
  public currentUser: IUser;
  public jobId: number = 0;
  public orderId: number;
  public jobInfo: IJobScreen = null;
  public previousInboxType: number = null;
  public previousUrl: string = null;
  public isLoadingSaveExpense = false;

  public pickupContactEdit: boolean = false;
  public dropoffContactEdit: boolean = false;

  public jobInfoEarliestPickUpTime = null;
  public jobInfoLatestPickUpTime = null;
  public jobInfoEarliestDropOffTime = null;
  public jobInfoLatestDropOffTime = null;
  public editAnswers = {};
  public showChecklistStepPhotosDialog: boolean = false;

  //inbox types
  public type_inboxSupplierToBeConfirmed = 1;
  public type_inboxAwaitingStart = 2;
  public type_inboxCompletedJobs = 3;
  public type_inboxCombine = 4;
  public type_inboxWaitingForApproval = 5;
  public type_inboxLive = 6;
  public type_inboxUrgentJobs = 7;
  public type_inboxClosed = 8;
  public type_inboxCancelled = 9;

  public selectedOrder: IOrderScreen = null;
  public _selectedTask: ITaskInfo = null;

  public editTaskScheduleVisible: boolean = false;

  public verifiedImageUrl = 'assets/images/verified.png';
  editingAdditionalCharge: boolean;
  private logger: TaggedLogger;
  private currentTaskStep: ITaskOrganisationSpecificSteps = null;
  private currentTaskStepAnswer: ITaskStepAnswer = null;
  public showChecklistStepAnswerPhotosDialog: boolean;
  private previewTaskStep: ITaskOrganisationSpecificSteps;

  showRejectionDialog: boolean = false;

  showRequestInfoDialog: boolean;
  public jobAllocationInfo: { price: number; id: number; tasks: ITaskInfo[] };
  currentOrder: any;

  public taskSiteOpeningHours: ITaskSiteOpeningHoursDisplay;

  TaskSupplierScheduleSource = TaskSupplierScheduleSource;

  @HostListener('window:scroll', [])
  onWindowScroll() {
    this.recalculateHeaderBarDimension();
  }

  @HostListener('window:resize', [])
  onWindowResize() {
    setTimeout(() => {
      this.recalculateHeaderBarDimension();
    }, 500);
  }

  private recalculateHeaderBarDimension() {
    const jobScreenHeaderBar = document.getElementById('job-screen-header-bar');
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    const parentWidth = jobScreenHeaderBar.parentElement.clientWidth - 40;

    if (jobScreenHeaderBar) {
      if (scrollPosition > 0) {
        jobScreenHeaderBar.style.position = 'fixed';
        jobScreenHeaderBar.style.zIndex = '99';
        jobScreenHeaderBar.style.width = `${parentWidth}px`;
      } else {
        jobScreenHeaderBar.style.position = 'relative';
        jobScreenHeaderBar.style.width = 'auto';
        jobScreenHeaderBar.style.left = 'auto';
      }
    }
  }

  @HostListener('click', ['$event.target'])
  onClick(btn) {
    if (!this.hasClassInElementOrParents(btn, 'showPremiums')) {
      this.showCustomerPremiums = false;
      this.showSupplierPremiums = false;
    }
  }

  @Input('selectedTask')
  public set selectedTask(value: ITaskInfo) {
    this._selectedTask = value;

    this.updateSelectedTaskSteps();
    this.updateImages(this._selectedTask, StepType.PickupPhoto, true);
    this.updateImages(this._selectedTask, StepType.DropOffPhoto, true);

    this.driverChecklist = this.getDriverCheckList(this._selectedTask);

    this.driverChecklistPickUp = this.getDriverChecklistPickUp(this.driverChecklist);
    this.driverChecklistDropOff = this.getDriverChecklistDropOff(this.driverChecklist);

    this.siteOpeningHoursService
      .getTaskSiteOpeningHoursDisplay(value.id)
      .then((r) => (this.taskSiteOpeningHours = r));
    if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      this.agreementService
        .isCustomerAbleToCancelOrder(this.organisationId, this.selectedTask.id)
        .then((x) => {
          this.isCustomerAbleToCancelOrder = x;
        });
    }

    if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      void this.getInspectionHandoverDetails(value.id);
    }
  }

  public get isAddEditPhotoDialogVisible(): boolean {
    return this.isShowAddPhotoToTaskStep || this.isShowEditPhotoToTaskStep;
  }

  public set isAddEditPhotoDialogVisible(val: boolean) {
    this.isShowAddPhotoToTaskStep = this.isShowEditPhotoToTaskStep = val;
    this.multipleFileUploadResult = [];
  }

  public get selectedTask(): ITaskInfo | any {
    return this._selectedTask;
  }

  public get driverName(): string {
    return this.driverNameTitle(false);
  }

  public get driverTitle(): string {
    return this.driverNameTitle(true);
  }

  public get supplierAssigned(): boolean {
    return this.jobInfo && this.jobInfo.supplierId != null;
  }

  public get allowAddDriverNotes(): boolean {
    return this.organisationTypeId !== OrganisationTypeEnum.Supplier;
  }

  public driverChecklist: ITaskStepNode[] = [];
  public driverChecklistPickUp: ITaskStepNode[] = [];
  public driverChecklistDropOff: ITaskStepNode[] = [];
  public allowDriverChecklistEdit: boolean = false;
  public isDriverChecklistManualEdit: boolean = false;

  public isBusyChangeSupplierPriceAccount: boolean = false;
  public searchingAccountCodes: boolean = false;
  public accountCodes: IAccountCode[] = [];
  public selectedAccountCode: IAccountCode = null;

  public searchingRegions: boolean = false;
  public regions: IRegion[] = [];
  public selectedRegion: IRegion = null;

  public searchingDepots: boolean = false;
  public depots: IDepot[] = [];
  public selectedDepot: IDepot = null;

  public searchingTags: boolean = false;
  public jobTags: any[] = [];
  public selectedJobTags: any[] = [];

  public supplierPrice: number = 0;

  public secondaryCancellationDescription: string;
  public orderCustomerPrice: number = 0;
  public orderInspectionCharge: number;
  public orderShowInspectionCharge: boolean;
  public orderTags: any[] = [];
  public selectedOrderTags: any[] = [];
  public isBusyEditOrder: boolean = false;
  public isOverridePrice: boolean = false;
  public toDisplaySecondaryCancellation: boolean = false;
  public toDisplaySecondaryCancellationStatusDescription: boolean = false;
  public purchaseOrderNo: string;

  public suppliers: ICustomer[] = [];
  public selectedSupplier: ICustomer = null;
  public searchingSuppliers: boolean = false;
  public isBusyChangeSupplierDetails: boolean = false;

  public searchingDrivers: boolean = false;
  public searchingTrucks: boolean = false;
  public isBusyChangeDriverTruck: boolean = false;
  public selectedDriver: IDriver = null;
  public selectedTruck: ITrucks = null;
  public drivers: IDriver[] = [];
  public trucks: ITrucks[] = [];

  public isSendMessageToDriver: boolean = false;
  public isBusySendMessageToDriver: boolean = false;
  public smsMessage: string = '';
  public isReloadDriverMessages: boolean = false;

  public isBusyChangeJobDetails: boolean = false;
  public jobDetailsForm: UntypedFormGroup;
  public pickupForm: UntypedFormGroup;
  public dropoffForm: UntypedFormGroup;
  public heldEndDate: Date;
  public exisingCapacityEndDate: Date;
  public scheduledDate: Date;
  public buyItNowPrice: number = null;
  public existingCapacityPrice: number = null;
  //public supplierPaid: number = null;
  public insuranceCost: number = null;
  public insuranceProviderId: number;

  public isBusyAddDriverNotes: boolean = false;
  public newNoteDescription: string = '';
  public newNoteIsPickup: boolean = true;
  public isReloadNotes: boolean = false;

  public jobTimelineCurrentStep: number = -1;
  public jobTimelineSteps: any[] = [];

  public taskTimelineCurrentStep: number = -1;
  public taskTimelineSteps: any[] = [];

  public directedSuppliers: any[] = [];
  public organisationId: number;
  public isAccessDenied: boolean = false;

  //cancel order
  public cancelOrderAction_None = 0;
  public cancelOrderAction_AbortFee = 1;
  public cancelOrderAction_Cancel3Fee = 2;
  public cancelOrderAction_Cancel7Fee = 3;
  public cancelOrderAction_CancelNoFee = 4;
  public cancelOrderAction_CustomerHoursAtWhichCap1Charged = 5;
  public cancelOrderAction_CustomerHoursAtWhichCap2Charged = 6;
  public cancelOrderAction_CustomerCap1 = 7;
  public cancelOrderAction_CustomerCap2 = 8;

  //cancel job
  public cancelJobAction_None = 0;
  public cancelJobAction_Cancel3Warn = 1;
  public cancelJobAction_CancelNoWarn = 2;

  public jobStatus_Completed = JobStatus.Completed;
  public jobStatus_Invoiced = JobStatus.Invoiced;
  public jobStatus_Closed = JobStatus.Closed;
  public jobStatus_Cancelled = JobStatus.Cancelled;
  public jobStatus_Created = JobStatus.Created;
  public jobStatus_Started = JobStatus.Started;
  public jobStatus_Paused = JobStatus.Paused;

  public additionalChargeStatusEnum = AdditionalChargeStatusEnum;

  public jobStatus = {
    Completed: this.jobStatus_Completed,
    Invoiced: this.jobStatus_Invoiced,
    Closed: this.jobStatus_Closed,
    Cancelled: this.jobStatus_Cancelled,
    Created: this.jobStatus_Created,
    Started: this.jobStatus_Started,
    Paused: this.jobStatus_Paused,
  };

  public cancelAction: number = this.cancelOrderAction_None;
  public customerChargedPrice: number;
  public newCustomerChargedPrice: number;
  public supplierChargedPrice: number;
  public newSupplierChargedPrice: number;

  public showCancelOrderDialog: boolean = false;
  public showCancelJobDialog: boolean = false;
  public showCancelOrderVmeDialog: boolean = false;
  public showCancelJobVmeDialog: boolean = false;

  public pickupPhone1Error: string;
  public pickupPhone2Error: string;
  public dropoffPhone1Error: string;
  public dropoffPhone2Error: string;
  //

  //edit locations
  public showEditLocationsDialog: boolean = false;
  //

  //impersonate
  public showImpersonateSupplierDialog: boolean = false;
  public showImpersonateCustomerDialog: boolean = false;
  public impersonateSupplierUsers: SelectItem[] = [];
  public impersonateCustomerUsers: SelectItem[] = [];
  public selectedImpersonateUser: SelectItem;

  public showPostcodePremium: boolean = false;

  @ViewChild('newDisputeControl') newDisputeControl: NewDisputeComponent;

  // Disputes
  // public disputePage: IPageResult<IDispute> = {
  //     items: [],
  //     total: 0
  // };

  public isLoadedDispute: boolean = false;
  public filterVal: IDisputeFilter;
  public disputeFilter: Subject<IDisputeFilter>;
  public selectedDispute = null;

  //Additional Cost
  public AdditionalChargeType = AdditionalChargeTypeEnum;

  // set when an additional charge is being edited for approval
  public isWaitingTimeCharge;
  public isChargingTimeCharge;

  // if true use customer rates on additional cost charge calculations
  public isCustomerCharge = true;
  public chargeIndex = 0;

  public isShowAdditionalCost: boolean = false;
  public additionalChargeTypes: SelectItem[] = [];
  public taskNumbers: SelectItem[] = [];
  public additionalCharge: any = {};
  public selectedAdditionalCharge: ICustomerAdditionalCharge;
  public additionalChargePage: IPageResult<ICustomerAdditionalCharge> = { items: [], total: 0 };

  public isShownPopupPhoto: boolean = false;
  public loadingPopupPhoto: boolean = false;
  public allowClosePopupPhoto: boolean = false;
  public popupPhotoUrl: string;
  public processingApproval: boolean = false;
  public cancelChargedPrice: number;
  public searchOrderTags: any;
  public isInvoiced: boolean;
  public isCustomerInvoiced: boolean;
  public isSupplierInvoiced: boolean;
  public taskExceededExpenseUploadThreshold: boolean = false;

  // Company information link data
  customerLinkData: any[];

  //Late status
  //Note: statuses are hardcoded. No DB table for now
  private lateStatuses: any[] = [
    { id: 1, title: 'Traffic' },
    { id: 2, title: 'Waiting for Vehicle' },
    { id: 3, title: 'Mobile App/Platform' },
    { id: 4, title: 'Engineius Agreed' },
    { id: 5, title: 'Site Closed' },
    { id: 6, title: 'Incorrect Location' },
    { id: 7, title: 'Vehicle Not On Site' },
    { id: 8, title: 'Breakdown' },
    { id: 9, title: 'Other' },
  ];
  lateStatusesSelectItems: SelectItem[];
  isShownLateStatusesDialog: boolean;
  lateStatusesEdited: any = {};

  //odometer
  isShownOdometerDialog: boolean;
  odometerEdited: IOdometerReading = <any>{};

  //fuel
  fuelLevelsSelectItems: SelectItem[];

  // customer order departments
  public searchingOrderDepartments: boolean = false;
  public customerDepartments: IDepartment[] = [];
  public selectedOrderDepartment: IDepartment = null;

  //customer order department teams
  public searchingDepartmentTeams: boolean = false;
  public departmentTeams: ITeam[] = [];
  public selectedOrderTeam: ITeam = null;

  //pause implementation
  public showPauseReasonDialog: boolean = false;
  public isJobPaused: boolean = false;
  public pauseReasons: SelectItem[] = [
    { value: 7, label: 'Awaiting Authorisation' },
    { value: 8, label: 'Awaiting Release' },
    { value: 1, label: 'Break' },
    { value: 10, label: 'Breakdown' },
    { value: 2, label: 'Overnight Parking' },
    { value: 3, label: 'Start Another Task' },
    { value: 9, label: 'Valet' },
    { value: 5, label: 'Vehicle Charging' },
    { value: 6, label: 'Vehicle Fuelling' },
  ];

  pauseReason: string = '';
  pauseReasonId: number = 0;
  selectedStep: ITaskStepNode;
  selectedStepPhotos: string[];
  public showStepPhotosDialog: boolean;
  showCurrentLocationDialog: boolean;
  currentLocation: MarkerPoint;
  cancelTime: number;

  telephonyEventSub: Subscription;
  orgSelecEventSub: Subscription;
  showAllocateJobDialog = false;
  allocateSupplier = null;
  allocationPrice = 0;
  allocationSupplierCard: ISupplierCard;

  tasksRequireFailureReason = false;
  delayFailures: ITaskDelayFailure[];

  public showValidationResult: boolean = false;

  //Expense images have to be an array because ngx gallery accept an array, but it just 1 photo at a time :)
  expenseImages: INgxGalleryImage[] = [];
  public expenseGalleryOptions = [
    {
      lazyLoading: false,
      image: false,
      startIndex: 0,
      showPreview: true,
      previewZoom: true,
      preview: true,
      zoomInIcon: 'fa fa-plus',
      zoomOutIcon: 'fa fa-minus',
      rotateLeftIcon: 'fa fa-rotate-left',
      rotateRightIcon: 'fa fa-rotate-right',
      rotate: true,
    },
  ];

  //MoDel PIN
  public isShownMoDelPin: boolean;
  public moDelPin: string;

  //Is customer able to cancel order
  private isCustomerAbleToCancelOrder = false;

  //Release Code
  public isShownReleaseCode: boolean;
  public releaseCode: string;

  private photoAreas: IPhotoArea[];

  //Inspection Handover
  inspectionHandover: JobScreenInspectionHandover;
  selectedMedia: JobScreenInspectionHandoverMedia;
  isViewInspectionRecordingVisible: boolean = false;
  inspectionHandoverLoading: boolean = false;

  public orderSequencingEnabled: boolean;
  public taskSequenceEditorVisible: boolean;

  isImpersonating: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public messageService: MessageService,
    private additionalChargeService: AdditionalChargeService,
    private userService: UserService,
    private jobService: JobsService,
    private accountCodeService: AccountCodeService,
    private supplierService: SupplierService,
    private driverService: IDriverService,
    private trucksService: TrucksService,
    private orderTagService: OrderTagService,
    private http: HttpClient,
    private disputeService: DisputeService,
    private orderService: OrderService,
    private location: Location,
    private inboxBackService: InboxBackService,
    private fileStorageService: FileStorageService,
    private fb: UntypedFormBuilder,
    private confirmationService: ConfirmationService,
    private vehicleService: VehicleService,
    private eRegionService: EngineiusRegionService,
    private settingsService: SettingsService,
    private insuranceProviderService: InsuranceProvidersService,
    private orgService: OrganisationsService,
    private incidentsService: IncidentsService,
    private organisationSpecificStepsService: OrganisationSpecificStepsService,
    public inputHelper: InputHelper,
    public checklistService: ChecklistService,
    public taskService: TaskService,
    public jobUnassignedReasonService: JobUnassignedReasonService,
    loggerService: LoggerService,
    private eventService: EventService,
    private vmeInboxjobsService: VmeInboxJobsService,
    private jobDeploymentService: JobsDeploymentService,
    private siteOpeningHoursService: SiteOpeningHoursService,
    private agreementService: AgreementService,
    public telephonyService: TelephonyService,
    private photoAreaService: PhotoAreaService,
    private jobAnalyticsService: JobAnalyticsService,
    private fuelLevelService: FuelLevelService,
  ) {
    this.logger = loggerService.taggedLogger('component:job-screen');
    this.previousUrl = this.inboxBackService.getUrl();
    this.previousInboxType = this.inboxBackService.getTab();

    this.telephonyEventSub = this.eventService.InternalTelephonyEvent.subscribe(
      (event: ITelephonyEvent) => {
        if (event.eventType == TelephonyEventEnum.CallLogged) {
          // refresh history section
          this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
        }
      },
    );

    this.orgSelecEventSub = this.eventService.OrganisationSelectorEvent.subscribe(
      (event: IOrganisationSelectorEvent) => {
        if (event.eventType == OrganisationSelectorEventTypeEnum.DeploySupplierSelected) {
          this.allocateSupplier = event.args;

          this.allocationPrice = !this.jobInfo.buyItNowPriceOriginal
            ? this.jobInfo.buyItNowPrice
            : this.jobInfo.buyItNowPriceOriginal;

          this.jobAllocationInfo.price = this.allocationPrice;

          // get the supplier card so we can show lack of suitability and warn user when allocating
          // this.jobDeploymentService
          //   .getSupplierCard(this.jobId, this.allocateSupplier.id)
          //   .then((res) => {
          //     this.allocationSupplierCard = res;
          //   });

          this.showAllocateJobDialog = true;
        }
      },
    );
  }

  public jobAssociatedWithCall = false;
  delayFailureRefreshSub: Subscription;

  async ngOnInit() {
    // broadcast an event so the telephony component can respond with an event if we are in a call
    this.eventService.JobViewEvent.emit();
    this.initRoles();

    this.loadPhotoAreas();

    this.pickupForm = this.fb.group({
      pickupForename: new UntypedFormControl(''),
      pickupSurname: new UntypedFormControl(''),
      pickupEmail: new UntypedFormControl(''),
      pickupSecondaryEmail: new UntypedFormControl(null, [Validators.email]),
      pickupPhone1: new UntypedFormControl('', (validator: UntypedFormControl) => {
        this.pickupPhone1Error = '';

        if (validator.value && !this.validMobileNumber(validator.value)) {
          let error = 'Invalid Mobile Number';
          this.pickupPhone1Error = error;

          return { valid: false, error: error };
        }

        return null;
      }),

      pickupPhone2: new UntypedFormControl('', (validator: UntypedFormControl) => {
        this.pickupPhone2Error = '';

        if (validator.value && !this.validPhoneNumber(validator.value)) {
          let error = 'Invalid Phone Number';
          this.pickupPhone2Error = error;

          return { valid: false, error: error };
        }

        return null;
      }),
    });

    this.dropoffForm = this.fb.group({
      dropoffForename: new UntypedFormControl(''),
      dropoffSurname: new UntypedFormControl(''),
      dropoffEmail: new UntypedFormControl(''),
      dropoffSecondaryEmail: new UntypedFormControl(null, [Validators.email]),
      dropoffPhone1: new UntypedFormControl('', (validator: UntypedFormControl) => {
        this.dropoffPhone1Error = '';

        if (validator.value && !this.validMobileNumber(validator.value)) {
          let error = 'Invalid mobile Number';
          this.dropoffPhone1Error = error;

          return { valid: false, error: error };
        }

        return null;
      }),

      dropoffPhone2: new UntypedFormControl('', (validator: UntypedFormControl) => {
        this.dropoffPhone2Error = '';

        if (validator.value && !this.validPhoneNumber(validator.value)) {
          let error = 'Invalid Phone Number';
          this.dropoffPhone2Error = error;

          return { valid: false, error: error };
        }

        return null;
      }),
    });

    this.jobDetailsForm = this.fb.group({
      exisingCapacityEndDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      heldEndDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      buyNowOpensDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        return null;
      }),
      scheduledDate: new UntypedFormControl('', (validator: UntypedFormControl) => {
        if (!validator.value) {
          return { valid: false, error: 'Is required' };
        }

        // Ignore time aspect
        var JobEarliest = new Date(this.jobInfoEarliestPickUpTime);
        JobEarliest.setHours(0, 0, 0, 0);
        var ValidDate = new Date(validator.value);
        ValidDate.setHours(0, 0, 0, 0);

        if (ValidDate < JobEarliest) {
          validator.markAsDirty();
          return { valid: false, error: 'This value must be greater than earliest task date' };
        }

        var JobLatest = new Date(this.jobInfoLatestDropOffTime);
        JobLatest.setDate(JobLatest.getDate() + 7);
        JobLatest.setHours(0, 0, 0, 0);

        if (ValidDate > JobLatest) {
          validator.markAsDirty();
          return {
            valid: false,
            error: 'This value must be within 7 days of the latest task date',
          };
        }

        return null;
      }),
      buyItNowPrice: new UntypedFormControl(0, null),
      existingCapacityPrice: new UntypedFormControl(0, null),
      supplierPrice: new UntypedFormControl(0, null),
      insuranceCost: new UntypedFormControl(0, null),
      insuranceProviderId: new UntypedFormControl(null),
    });

    this.jobId = parseInt(this.route.snapshot.paramMap.get('id'));
    this.orderId = parseInt(this.route.snapshot.paramMap.get('orderId'));

    if (this.jobId != 0) {
      this.Initialize().then(() => {
        this.initDefaultFilter();
        this.loadAdditionalCharges();
      });
    }

    this.initEngineiusTaskTypes();
    this.initLateStatuses();
    this.initFuelLevels();
    this.initRegStatuses();

    this.settingsService.GetSystemSettings().then((data: ISystemSettings) => {
      // store abort hours to disable button with tooltip
      this.cancelTime = data.abortTime;
    });

    this.delayFailureRefreshSub = this.eventService.DelayFailureRefreshEvent.subscribe(() => {
      if (this.jobInfo) {
        this.checkTasksRequireFailureReason(this.jobInfo);
      }
    });

    this.isImpersonating = (await this.userService.isUserImpersonating()).isImpersonating;
  }

  ngOnDestroy() {
    if (this.delayFailureRefreshSub) {
      this.delayFailureRefreshSub.unsubscribe();
    }
    if (this.telephonyEventSub) {
      this.telephonyEventSub.unsubscribe();
    }
    if (this.orgSelecEventSub) {
      this.orgSelecEventSub.unsubscribe();
    }
  }

  toDisplayDriverNotes(): boolean {
    if (this.organisationTypeId !== OrganisationTypeEnum.Supplier) {
      return true;
    }

    if (!this.jobInfo?.supplier) {
      return false;
    }

    if (this.jobInfo.supplierId === this.currentUser.organisationId) {
      /*var endMovementStep = this.driverChecklistDropOff.find((x) => x.stepType === 6);
      if (!endMovementStep || !endMovementStep.isComplete) {
        return true;
      }*/
      if (
        this.jobInfo.statusId !== this.jobStatus_Cancelled &&
        this.jobInfo.statusId !== this.jobStatus_Closed &&
        this.jobInfo.statusId !== this.jobStatus.Completed
      ) {
        return true;
      }
    }

    return false;
  }

  Initialize() {
    this.isBusy = true;

    this.userService.getLoggedUser().then((res) => (this.currentUser = res));
    return this.userService
      .getCurrentUserOrg()
      .then((res) => {
        this.isBusy = false;
        if (res) {
          this.organisationTypeId = res.organisationTypeId;
          this.organisationId = res.id;

          this.LoadJob();

          if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
            this.initInsuranceProviders(this.jobId);
          }

          if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
            //customer
            this.orderService.getCancelOrderAction(this.orderId).then((a) => {
              this.cancelAction = a ? a : this.cancelOrderAction_None;
            });
          } else if (this.organisationTypeId == OrganisationTypeEnum.Supplier) {
            //supplier
            this.orderService.getCancelJobAction(this.jobId).then((a) => {
              this.cancelAction = a ? a : this.cancelJobAction_None;
            });
          }
        }
      })
      .catch((x) => {
        this.isBusy = false;
        this.messageService.add(
          Message.Error('Error loading organization information.', 'Load User Info'),
        );
      });
  }

  public driverNameTitle(title: boolean) {
    let name: string = '';
    let driver = this._selectedTask.driver;
    if (driver != null) {
      let fullName = driver.firstName + ' ' + driver.surname;
      let nick =
        typeof driver.nickName != 'undefined' && driver.nickName ? driver.nickName : fullName;

      if (driver) {
        if (title) {
          name = this.organisationTypeId == OrganisationTypeEnum.Supplier ? fullName : nick;
        } else {
          name = this.organisationTypeId == OrganisationTypeEnum.Supplier ? nick : fullName;
        }
      }
    } else if (
      this.organisationTypeId == OrganisationTypeEnum.Customer ||
      this.organisationTypeId == OrganisationTypeEnum.Engineius
    ) {
      name =
        this.jobInfo.supplier?.name +
        (this.organisationTypeId == OrganisationTypeEnum.Engineius
          ? ' (driver not yet assigned)'
          : '');
    }
    return name;
  }

  getConfirmedDateTime() {
    return moment(this.jobInfo.confirmedDateTime).format('Do MMM YYYY HH:mm');
  }

  getStatus() {
    if (!this.jobInfo) {
      return '';
    }

    if (this.jobInfo.statusId == this.jobStatus_Created) {
      var now = new Date();
      var heldEndDate = new Date();
      var exisingCapacityEndDate = new Date();
      var scheduledDate = new Date();

      if (typeof this.jobInfo.heldEndDate == 'string') {
        heldEndDate = new Date(this.jobInfo.heldEndDate);
      } else {
        heldEndDate = this.jobInfo.heldEndDate;
      }

      if (typeof this.jobInfo.exisingCapacityEndDate == 'string') {
        exisingCapacityEndDate = new Date(this.jobInfo.exisingCapacityEndDate);
      } else {
        exisingCapacityEndDate = this.jobInfo.exisingCapacityEndDate;
      }

      if (typeof this.jobInfo.scheduledDate == 'string') {
        scheduledDate = new Date(this.jobInfo.scheduledDate);
      } else {
        scheduledDate = this.jobInfo.scheduledDate;
      }

      if (now < heldEndDate) {
        return 'Held';
      } else if (now >= heldEndDate && now < exisingCapacityEndDate) {
        return 'Existing Capacity';
      } else if (now >= exisingCapacityEndDate) {
        return 'Buy It Now';
      }
    }

    switch (this.jobInfo.statusId) {
      case this.jobStatus_Completed:
        return 'Completed';
      case this.jobStatus_Invoiced:
        return 'Invoiced';
      case this.jobStatus_Closed:
        return 'Closed';
      case this.jobStatus_Cancelled:
        return 'Cancelled';
      case this.jobStatus_Started:
        return 'Started';
      case this.jobStatus_Paused:
        return 'Paused';
    }

    return '';
  }

  getOrderStatus(orderStatusId): string {
    switch (orderStatusId) {
      case OrderStatus.Completed:
        return 'Completed';
      case OrderStatus.Invoiced:
        return 'Invoiced';
      case OrderStatus.Closed:
        return 'Closed';
      case OrderStatus.Cancelled:
        return 'Cancelled';
      case OrderStatus.Created:
        return 'Created';
      case OrderStatus.Started:
        return 'Started';
    }

    return '';
  }

  flatDeep(arr, d = 1) {
    return d > 0
      ? arr.reduce(
          (acc, val) => acc.concat(Array.isArray(val) ? this.flatDeep(val, d - 1) : val),
          [],
        )
      : arr.slice();
  }

  LoadJob() {
    this.isBusy = true;

    this.jobService
      .get(this.jobId)
      .then((job) => {
        this.getDirectedSuppliers();

        this.isBusy = false;
        this.jobInfo = job;

        this.jobAllocationInfo = {
          id: job.id,
          tasks: job.orders?.map((o) => o.tasks).reduce((acc, value) => acc.concat(value)),
          price: job.buyItNowPrice,
        };

        this.checkTasksRequireFailureReason(job);

        this.jobInfo.supplierPremiums = this.jobInfo.pricePremiums.filter(
          (x) => x.isSupplierPremium,
        );

        this.jobInfo.supplierPremium = this.jobInfo.supplierPremiums.reduce(
          (a, b) => a + b.premium,
          0,
        );

        this.jobInfo.supplierPremiumSummary = this.summarisePricePremiums(
          this.jobInfo.supplierPremiums,
        );

        this.jobInfo.customerPremiums = this.jobInfo.pricePremiums.filter(
          (x) => !x.isSupplierPremium,
        );

        this.jobInfo.customerPremium = this.jobInfo.customerPremiums.reduce(
          (a, b) => a + b.premium,
          0,
        );

        this.jobInfo.customerPremiumSummary = this.summarisePricePremiums(
          this.jobInfo.customerPremiums,
        );

        this.customerLinkData = uniqBy(job.orders, 'customer.name').map((x) => {
          return {
            label: x.customer.name,
            infoURL: x.customer.companyInfoURL,
            icon: 'pi pi-external-link',
            command: () => {
              this.linkToCustomerInfo(x.customer.companyInfoURL);
            },
          };
        });

        this.CheckAccess();

        if (this.jobInfo && this.jobInfo.orders && this.jobInfo.orders.length > 0) {
          if (this.orderId) {
            this.jobInfo.orders = this.jobInfo.orders.filter((x) => x.id == this.orderId);
          } else {
            if (
              this.organisationTypeId != 1 &&
              this.jobInfo.statusId != this.jobStatus_Closed &&
              this.jobInfo.statusId != this.jobStatus_Cancelled
            ) {
              this.jobInfo.orders = this.jobInfo.orders.filter(
                (x) => x.statusId != OrderStatus.Cancelled && x.statusId != OrderStatus.Closed,
              );
            }
          }

          if (this.jobInfo.orders.length > 0) {
            this.selectedOrder = this.jobInfo.orders[0];
          }
        }

        // if none of the orders have a postcode premium > 0 hide the column
        this.showPostcodePremium =
          this.organisationTypeId == OrganisationTypeEnum.Engineius &&
          this.jobInfo.orders.filter(
            (x) => x.customerPostcodePremium && x.customerPostcodePremium > 0.0,
          ).length > 0;

        this.fillJobTimeline();

        this.allowDriverChecklistEdit = this.organisationTypeId == OrganisationTypeEnum.Engineius;

        // If org type is VME then close appropriate incidents
        // commenting this unless ops ask for it to be reinstated since just looking at the job doesn't mean the message has been read
        //if (this.organisationTypeId == 1) {
        //    this.orderService.setReadMessageByVme({ id: this.jobId });
        //}

        this.hasInvoice();
        this.hasCustomerInvoice();
        this.hasSupplierInvoice();
        this.isJobPaused = this.jobInfo.statusId == this.jobStatus_Paused;

        this.orderSequencingEnabled = this.jobInfo.orders.some((x) => x.orderSequence != null);

        if (this.orderSequencingEnabled) {
          this.jobInfo.orders.forEach((order, index) => {
            order.localOrderSequence = index + 1;
          });
        }
        this.jobAnalyticsService.sendJobDetailsViewEvent(job, this.organisationTypeId);
      })
      .catch((x) => {
        this.isBusy = false;
        this.messageService.add(Message.Error('Error loading job information.', 'Load Job Info'));
      });
  }

  private summarisePricePremiums(premiums: IPricePremium[]): IPricePremiumSummary[] {
    let totals = premiums.reduce((a, b) => {
      const name = b.pricePremiumCategory.categoryName;
      if (!a.hasOwnProperty(name)) {
        a[name] = 0;
      }
      a[name] += b.premium;
      return a;
    }, {});

    var returnVal = Object.keys(totals).map((k) => {
      return { categoryName: k, premium: totals[k] };
    });

    return returnVal;
  }

  private checkTasksRequireFailureReason(job: IJobScreen) {
    const tasks = flatMap(job.orders.map((x) => x.tasks));
    this.tasksRequireFailureReason = tasks.some((x) => x.requiresDelayFailureReason);
  }

  public CheckAccess() {
    if (!this.jobInfo || this.jobInfo.orders.length == 0) {
      return;
    }

    this.isAccessDenied = false;

    if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      //customer
      if (this.jobInfo.orders.length > 1) {
        var order = this.jobInfo.orders.find((x) => x.id == this.orderId);
        if (order && order.customerId != this.organisationId) {
          this.isAccessDenied = true;
        }
      } else if (this.jobInfo.orders[0].customerId != this.organisationId) {
        this.isAccessDenied = true;
      }
    } else if (this.organisationTypeId == OrganisationTypeEnum.Supplier) {
      //supplier
      if (
        this.jobInfo.supplier &&
        this.jobInfo.supplierId &&
        this.jobInfo.supplierId != this.organisationId
      ) {
        this.isAccessDenied = true;
      }
    }
  }

  linkToCustomerInfo(url: string) {
    if (!url || url.trim() === '') {
      if (this.customerLinkData && this.customerLinkData.length > 0) {
        url = this.selectedOrder?.customer.companyInfoURL;
      }

      if (!url && this.customerLinkData && this.customerLinkData.length > 0) {
        url = <string>this.customerLinkData[0].infoURL;
      }

      if (!url) {
        this.messageService.clear();
        this.messageService.add(
          Message.Info('The selected company does not have an information URL specified'),
        );
        return;
      }
    }

    this.openInNewTab(url);
  }

  openInNewTab(href) {
    Object.assign(document.createElement('a'), {
      target: '_blank',
      href,
    }).click();
  }

  fillJobTimeline() {
    this.jobTimelineSteps = [];
    if (!this.jobInfo) {
      return;
    }

    if (this.jobInfo.createdOn) {
      this.jobTimelineSteps.push({
        name: 'Job submitted',
        date: new Date(this.jobInfo.createdOn),
        isCustomer: false,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Job submitted', date: null, isCustomer: false });
    }

    if (this.jobInfo.orders.length == 1) {
      if (this.jobInfo.heldEndDate) {
        this.jobTimelineSteps.push({
          name: 'Released from hold',
          date: new Date(this.jobInfo.heldEndDate),
          isCustomer: false,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Released from hold', date: null, isCustomer: false });
      }
    }

    if (this.jobInfo.exisingCapacityEndDate) {
      this.jobTimelineSteps.push({
        name: 'Buy it now opens',
        date: new Date(this.jobInfo.exisingCapacityEndDate),
        isCustomer: false,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Buy it now opens', date: null, isCustomer: false });
    }

    this.jobInfoEarliestPickUpTime = null;
    this.jobInfoLatestPickUpTime = null;
    this.jobInfoEarliestDropOffTime = null;
    this.jobInfoLatestDropOffTime = null;

    this.jobInfo.orders.forEach((order) => {
      order.tasks.forEach((task) => {
        if (
          this.jobInfoEarliestPickUpTime == null ||
          this.jobInfoEarliestPickUpTime > new Date(task.pickupEarliestDateTime)
        ) {
          this.jobInfoEarliestPickUpTime = new Date(task.pickupEarliestDateTime);
        }

        if (
          this.jobInfoLatestPickUpTime == null ||
          this.jobInfoLatestPickUpTime < new Date(task.pickupLatestDateTime)
        ) {
          this.jobInfoLatestPickUpTime = new Date(task.pickupLatestDateTime);
        }

        if (
          this.jobInfoEarliestDropOffTime == null ||
          this.jobInfoEarliestDropOffTime > new Date(task.dropoffEarliestDateTime)
        ) {
          this.jobInfoEarliestDropOffTime = new Date(task.pickupEarliestDateTime);
        }

        if (
          this.jobInfoLatestDropOffTime == null ||
          this.jobInfoLatestDropOffTime < new Date(task.dropoffLatestDateTime)
        ) {
          this.jobInfoLatestDropOffTime = new Date(task.dropoffLatestDateTime);
        }
      });
    });

    if (this.jobInfoEarliestPickUpTime) {
      this.jobTimelineSteps.push({
        name: 'Pick-up earliest',
        date: this.jobInfoEarliestPickUpTime,
        isCustomer: true,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Pick-up earliest', date: null, isCustomer: true });
    }

    if (this.jobInfoEarliestDropOffTime > this.jobInfoLatestPickUpTime) {
      if (this.jobInfoLatestPickUpTime) {
        this.jobTimelineSteps.push({
          name: 'Pick-up latest',
          date: this.jobInfoLatestPickUpTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Pick-up latest', date: null, isCustomer: true });
      }

      if (this.jobInfoEarliestDropOffTime) {
        this.jobTimelineSteps.push({
          name: 'Drop-off earliest',
          date: this.jobInfoEarliestDropOffTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Drop-off earliest', date: null, isCustomer: true });
      }
    } else {
      if (this.jobInfoEarliestDropOffTime) {
        this.jobTimelineSteps.push({
          name: 'Drop-off earliest',
          date: this.jobInfoEarliestDropOffTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Drop-off earliest', date: null, isCustomer: true });
      }

      if (this.jobInfoLatestPickUpTime) {
        this.jobTimelineSteps.push({
          name: 'Pick-up latest',
          date: this.jobInfoLatestPickUpTime,
          isCustomer: true,
        });
      } else {
        this.jobTimelineSteps.push({ name: 'Pick-up latest', date: null, isCustomer: true });
      }
    }

    if (this.jobInfoLatestDropOffTime) {
      this.jobTimelineSteps.push({
        name: 'Drop-off latest',
        date: this.jobInfoLatestDropOffTime,
        isCustomer: true,
      });
    } else {
      this.jobTimelineSteps.push({ name: 'Drop-off latest', date: null, isCustomer: true });
    }

    var today = new Date();
    var i = 0;
    var isExistDate = false;

    for (; i < this.jobTimelineSteps.length; i++) {
      if (
        this.jobTimelineSteps[i].date &&
        this.jobTimelineSteps[i].date.getTime() > today.getTime()
      ) {
        isExistDate = true;
        break;
      }
    }

    if (!isExistDate) {
      i = 0;
      for (; i < this.jobTimelineSteps.length; i++) {
        if (this.jobTimelineSteps[i].date == null) {
          break;
        }
      }
    }

    this.jobTimelineSteps.splice(i, 0, {
      name: 'Current date time',
      date: today,
      isCustomer: true,
    });
    this.jobTimelineCurrentStep = i + 1;
  }

  updateSelectedTaskSteps() {
    this.taskTimelineCurrentStep = -1;
    this.taskTimelineSteps = [];

    var tempTaskTimelineSteps = [];

    if (this.selectedTask) {
      //this.taskTimelineCurrentStep = 1;
      var currentDay = new Date();

      tempTaskTimelineSteps.push({
        name: 'Pick-up earliest',
        date: new Date(this.selectedTask.pickupEarliestDateTime),
        isCustomer: true,
      });
      tempTaskTimelineSteps.push({
        name: 'Pick-up latest',
        date: new Date(this.selectedTask.pickupLatestDateTime),
        isCustomer: true,
      });
      tempTaskTimelineSteps.push({
        name: 'Drop-off earliest',
        date: new Date(this.selectedTask.dropoffEarliestDateTime),
        isCustomer: true,
      });
      tempTaskTimelineSteps.push({
        name: 'Drop-off latest',
        date: new Date(this.selectedTask.dropoffLatestDateTime),
        isCustomer: true,
      });

      var startTaskStep = this.selectedTask.completeTaskSteps.find(
        (x) => x.stepType == StepType.StartTask,
      );
      if (startTaskStep)
        tempTaskTimelineSteps.push({
          name: 'Heading to Pick-up Location',
          date: new Date(startTaskStep.completedDateTime),
          isCustomer: false,
        });

      var startMovementStep = this.selectedTask.completeTaskSteps.find(
        (x) => x.stepType == StepType.StartMovement,
      );
      if (startMovementStep)
        tempTaskTimelineSteps.push({
          name: 'Start Movement',
          date: new Date(startMovementStep.completedDateTime),
          isCustomer: false,
        });

      var endMovementStep = this.selectedTask.completeTaskSteps.find(
        (x) => x.stepType == StepType.EndMovement,
      );
      if (endMovementStep)
        tempTaskTimelineSteps.push({
          name: 'End Movement',
          date: new Date(endMovementStep.completedDateTime),
          isCustomer: false,
        });

      var completeTaskStep = this.selectedTask.completeTaskSteps.find(
        (x) => x.stepType == StepType.CompleteTask,
      );
      if (completeTaskStep)
        tempTaskTimelineSteps.push({
          name: 'Complete Task',
          date: new Date(completeTaskStep.completedDateTime),
          isCustomer: false,
        });
    }

    this.taskTimelineSteps = tempTaskTimelineSteps.sort((x, y) => {
      if (x.date > y.date) return 1;
      else if (x.date < y.date) return -1;
      else return 0;
    });

    this.taskTimelineCurrentStep = 1;
    this.taskTimelineSteps.forEach((x) => {
      if (x.date < currentDay) {
        this.taskTimelineCurrentStep++;
      }
    });
  }

  getCustomerTags(tags: IOrderTag[]) {
    if (!tags) {
      return '';
    }

    var descriptions = [];

    tags.forEach((x) => {
      descriptions.push(x.description);
    });

    return descriptions.join(', ');
  }

  selectTask(event) {
    this.selectedTask = event;
  }

  // images START
  pickupImages: IGalleryImage[] = [];
  dropoffImages: IGalleryImage[] = [];

  private updateImages(task: ITaskInfo, stepType: StepType, clearCachedUrl: boolean) {
    switch (stepType) {
      case StepType.PickupPhoto:
        this.pickupImages = this.getImages(
          task,
          StepType.PickupPhoto,
          StepType.PickupSignature,
          clearCachedUrl,
        );
        break;
      case StepType.DropOffPhoto:
        this.dropoffImages = this.getImages(
          task,
          StepType.DropOffPhoto,
          StepType.DropOffSignature,
          clearCachedUrl,
        );
        break;
    }
  }

  private getImages(
    task: ITaskInfo,
    photoStepType: number,
    signatureStepType: number,
    clearCachedUrl: boolean,
  ): IGalleryImage[] {
    let images = [];
    if (task?.completeTaskSteps) {
      var steps = task.completeTaskSteps.filter((s) => photoStepType == s.stepType);
      if (steps.length) {
        var step = steps[0];
        step.photos.forEach((photo) => {
          let condition = null;
          let conditionComment = null;
          for (let i = 0; i < step.areas.length; i++) {
            if (photo.photoArea == step.areas[i].photoArea) {
              condition = step.areas[i].condition;
              conditionComment = step.areas[i].comment;
              break;
            }
          }

          images.push({
            id: photo.id,
            objectKey: photo.photoLocation,
            source: photo.photoLocation,
            description: photo.description,
            isSignature: false,
            area: photo.photoArea,
            areaTitle: this.getAreaTitle(photo.photoArea),
            condition: condition,
            conditionTitle: this.getConditionTitle(condition),
            conditionComment: conditionComment,
            dateTime: photo.dateTime,
            clearCachedUrl: clearCachedUrl,
            extension: photo.extension,
          });
        });
      }

      steps = task.completeTaskSteps.filter((s) => signatureStepType == s.stepType);
      if (steps.length) {
        step = steps[0];
        var signatureTitle =
          signatureStepType == StepType.PickupSignature
            ? 'Pick-up Signature'
            : 'Drop-off Signature';
        step.signatures.forEach((photo) => {
          let condition = null;
          for (let i = 0; i < step.areas.length; i++) {
            if (photo.photoArea == step.areas[i].photoArea) {
              condition = step.areas[i].condition;
              break;
            }
          }

          images.push({
            id: photo.id,
            objectKey: photo.signatureLocation,
            source: photo.signatureLocation,
            isSignature: true,
            area: photo.photoArea,
            areaTitle: signatureTitle,
            dateTime: step.completedDateTime,
            extension: photo.extension,
          });
        });
      }
    }

    return images;
  }

  // images END

  sortDriverMessages(driverMessages: IDriverMessage[]): IDriverMessage[] {
    if (!driverMessages) return [];

    driverMessages.sort((a, b) => (a.created < b.created ? 1 : -1));
    return driverMessages;
  }

  getNotes(notes: ITaskNote[], isPickup: boolean) {
    if (!notes) return [];

    var result = [];
    notes.forEach((x) => {
      if (x.isPickUp == isPickup) {
        result.push(x);
      }
    });

    result.sort((x) => new Date(x.created).getTime());
    return result;
  }

  getAreaTitle(area): string {
    return this.photoAreas.find((x) => x.area == area).description;
  }

  getConditionTitle(condition): string {
    switch (condition) {
      case 0:
        return 'Good Condition';
      case 1:
        return 'Minor Damage';
      case 2:
        return 'Major Damage';
      case 3:
        return 'Uninspected';
      case 4:
        return 'Not Required';
      default:
        return '';
    }
  }

  initDefaultFilter() {
    this.filterVal = {
      first: 0,
      rows: 10,
      sortField: 'JobNumber',
      sortOrder: 1,
      filters: {
        jobNumber: {},
        customerCompanyName: {},
        supplierCompanyName: {},
      },
    };
  }

  loadAdditionalCharges() {
    if (this.jobId && this.jobId != 0) {
      this.additionalChargeService.getCustomerChargesByJob(this.jobId).then((result) => {
        this.additionalChargePage.items = result;
        this.additionalChargePage.total = result.length;
      });
    }
  }

  /* Deprecated ? */
  onDisputeGridLazyLoad(event: LazyLoadEvent) {
    this.filterVal.first = event.first;
    this.filterVal.rows = event.rows;
    this.filterVal.sortField = event.sortField;
    this.filterVal.sortOrder = event.sortOrder;
    // map grid lazy load event to filter
    this.disputeFilter.next(this.filterVal);
  }

  clearFilter(table: any) {
    table.sortField = '';
    this.initDefaultFilter();
    this.disputeFilter.next(this.filterVal);
  }

  search() {
    this.filterVal.first = 0; //fix error - when you set page > 1 and filter, then data not display
    this.disputeFilter.next(this.filterVal);
  }

  addDispute() {
    var orderList = [];
    this.jobInfo.orders.forEach((x) => {
      orderList.push({ id: x.id, name: x.orderNumber });
    });

    this.newDisputeControl.createNewDispute(this.jobId, orderList, this.orderId);
  }

  onDisputeCreated() {
    this.initDefaultFilter();
    this.disputeFilter.next(this.filterVal);
  }

  openDispute(disputeId) {
    this.router.navigate([`app/disputecs/${disputeId}`], {
      queryParams: { backTo: this.router.url },
    });
  }

  /**
   * Get the checklist items for a task
   * @param task The task
   * @return An array of task steps/checklist items
   */
  getDriverCheckList(task: ITaskInfo) {
    this.logger.debug('getDriverChecklist:', task);
    let result: ITaskStepNode[] = [];
    let order = 0;

    // If task or steps are falsy, return no steps, makes sense.
    if (!task || !task.taskSteps) {
      return [];
    }

    // Engineius Steps
    const pickupSteps = task.taskSteps
      .filter((x) => x.stepType !== StepType.Rating)
      // .filter(x => x.isPickUp)
      .sort((a, b) => a.order - b.order);

    const processedPickupSteps = pickupSteps.map((step) => {
      // find the completed step if the step is completed
      const completedStep = (task.completeTaskSteps || []).find(
        (completedStep) => completedStep.stepType === step.stepType,
      );
      // get its date
      const completedAt = completedStep ? floorDate(completedStep.completedDateTime) : null;
      // Get its pauses/resumes
      const pauses = completedStep ? completedStep.pauses : [];

      // Create a view model to display
      return {
        id: step.id,
        order: step.order,
        content: step.name,
        isPickup: step.isPickUp,
        source: '', // todo: is this used?
        isComplete: !!completedAt,
        date: completedAt,
        isTaskstep: true,
        stepType: step.stepType,
        isCustomStep: false,
        isOrganisationStep: false,
        minDate: new Date(), // todo: what's this used for?
        maxDate: new Date(), // todo: what's this used for?
        longitude: step.longitude,
        latitude: step.latitude,
        pauses,
      };
    });

    // Push the processed pickup steps
    result.push(...processedPickupSteps);

    // Custom Task Actions
    // the order property needs to come from the checklist task type instance order
    const mappedCustomSteps = task.customTaskActions.map((step) => {
      const completedAt = floorDate(step.completedDate);
      return {
        id: step.id,
        order: step.order,
        content: step.name,
        isPickup: step.isPickUp,
        date: completedAt,
        source: '',
        isComplete: !!completedAt,
        isTaskstep: false,
        isCustomStep: true,
        isOrganisationStep: false,
        minDate: new Date(),
        maxDate: new Date(),
        photoLocation: step.photoLocation,
        longitude: step.longitude,
        latitude: step.latitude,
      };
    });

    // Add custom pickup steps to the result array
    const defaultParentPickupStepIndex = result.findIndex(
      (step) => step.stepType === StepType.ArrivedToPickup,
    );
    const customPickupSteps = mappedCustomSteps.filter((step) => step.isPickup);
    result.splice(defaultParentPickupStepIndex + 1, 0, ...customPickupSteps);

    // Add custom dropoff steps to the result array
    const defaultParentDropoffStepIndex = result.findIndex(
      (step) => step.stepType === StepType.EndMovement,
    );
    const customDropoffSteps = mappedCustomSteps.filter((step) => !step.isPickup);
    result.splice(defaultParentDropoffStepIndex + 1, 0, ...customDropoffSteps);

    // Organisation Steps
    const organisationSpecificSteps = task.taskOrganisationSpecificSteps.map((step) => {
      const completedAt = floorDate(step.completedDateTime);
      return {
        id: step.id,
        order: step.stepOrder,
        content: step.name,
        isPickup: step.isPickUp,
        date: completedAt,
        source: '',
        isComplete: !!completedAt,
        isTaskstep: false,
        isCustomStep: false,
        isOrganisationStep: true,
        minDate: new Date(),
        maxDate: new Date(),
        taskOrganisationSpecificStep: step,
        longitude: step.longitude,
        latitude: step.latitude,
        parentEngineiusStepId: step.parentEngineiusStepId,
        pauses: step.pauses,
      };
    });

    // todo: typing is royally messed up here, come back and define the types properly.
    result = this.organisationSpecificStepsService.sortStepsWithEnhancedChecklistSteps(
      result,
      organisationSpecificSteps as any,
    ) as any;

    // Pauses:
    // Pauses are a child object of a step, on taskSteps they live on completeTaskSteps
    // On organisation steps it's taskOrganisationSpecificSteps
    // Both take the same form when they're there.
    // Also note that completedTaskStep.isPickUp is not correct, so completedTaskStep.isPickUp should come from taskSteps where id==id.

    result
      .filter((step) => step.pauses && step.pauses.length > 0)
      .forEach((step) => {
        // Add new child steps at index + 1 for the pauses
        const pauses = step.pauses.map((pause) => {
          const createdOn = floorDate(pause.createdOn);

          return {
            id: pause.id,
            // order:
            content: pause.name,
            isPickup: step.isPickup,
            source: '',
            isComplete: !!createdOn,
            date: createdOn,
            stepType: null,
            isTaskstep: step.isTaskstep,
            isCustomStep: step.isCustomStep,
            isOrganisationStep: step.isOrganisationStep,
            longitude: pause.longitude,
            latitude: pause.latitude,
            minDate: new Date(),
            maxDate: new Date(),
            isPaused: !pause.isResume,
            isPauseStep: true,
          };
        });

        // Now to find where it's going to go
        const stepIndex = result.findIndex((s) => s.id === step.id);
        // Splice them in
        result.splice(stepIndex + 1, 0, ...(pauses as any[]));
      });

    this.logger.debug('driver checklist:', result);

    return result;
  }

  onClickBack() {
    if (this.previousUrl) {
      this.router.navigate([this.previousUrl]);
    } else {
      this.location.back();
    }
  }

  showChangeSupplierPriceAccount() {
    this.isShowChangeSupplierPriceAccount = true;

    this.supplierPrice = this.jobInfo.supplierPrice;

    this.selectedAccountCode = null;
    this.accountCodes = [];

    this.selectedRegion = null;
    this.regions = [];

    this.selectedDepot = null;
    this.depots = [];

    this.jobTags = [];
    this.selectedJobTags = [];

    this.loadAccountCodes(this.jobInfo.supplierId, this.jobInfo);

    this.loadRegions(this.jobInfo.supplierId);

    this.loadTags(this.jobInfo.supplierId);
  }

  loadTags(supplierId) {
    if (supplierId == null) {
      return;
    }

    this.searchingTags = true;
    this.orderTagService
      .getForCustomer(supplierId)
      .then((result) => {
        this.searchingTags = false;
        var selectedJobTags = [];
        var items = [];
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.description, value: x });
          });

          if (this.jobInfo.jobTags != null) {
            items.forEach((x) => {
              this.jobInfo.jobTags.forEach((y) => {
                if (x.value != null && x.value.id == y.orderTagId) {
                  selectedJobTags.push(x.value);
                }
              });
            });
          }
        }

        this.jobTags = items;
        this.selectedJobTags = selectedJobTags;
      })
      .catch((x) => {
        this.searchingTags = false;
        this.jobTags = [];
        this.selectedJobTags = [];
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Job Tags, please try again.',
            'Load Job Tags',
          ),
        );
      });
  }

  loadAccountCodes(supplierId, item) {
    if (supplierId == null || item == null) {
      return;
    }

    this.searchingAccountCodes = true;
    this.accountCodeService
      .getForSupplier(supplierId)
      .then((result) => {
        var selectAccountCode = null;
        this.searchingAccountCodes = false;
        var items = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.name, value: x });
          });

          if (item.accountCodeList != null) {
            items.forEach((x) => {
              if (x.value != null && x.value.id == item.accountCodeList.id) {
                selectAccountCode = x.value;
              }
            });
          }
        }

        this.accountCodes = items;
        this.selectedAccountCode = selectAccountCode;
      })
      .catch((x) => {
        this.searchingAccountCodes = false;
        this.accountCodes = [];
        this.selectedAccountCode = null;
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Account Code, please try again.',
            'Load Account Codes',
          ),
        );
      });
  }

  loadRegions(supplierId) {
    if (supplierId == null) {
      return;
    }

    this.searchingRegions = true;
    this.supplierService
      .getRegionsForSupplier(supplierId)
      .then((result) => {
        this.searchingRegions = false;
        var selectRegion = null;

        var items = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.name, value: x });
          });

          if (this.jobInfo.region != null) {
            items.forEach((x) => {
              if (x.value != null && x.value.id == this.jobInfo.region.id) {
                selectRegion = x.value;
              }
            });
          }
        }

        this.regions = items;
        this.selectedRegion = selectRegion;
        if (selectRegion != null) {
          this.loadDepots(selectRegion.id);
        } else {
          this.regionChanged(null);
        }
      })
      .catch((x) => {
        this.searchingRegions = false;
        this.regions = [];
        this.selectedRegion = null;
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Regions, please try again.',
            'Load Regions',
          ),
        );
      });
  }

  regionChanged(event) {
    if (event == null || event.value == null) {
      this.selectedDepot = null;
      this.depots = [];
    } else {
      this.loadDepots(event.value.id);
    }
  }

  loadDepots(regiondId) {
    this.selectedDepot = null;
    this.depots = [];

    if (regiondId == null) {
      return;
    }

    this.searchingDepots = true;
    this.supplierService
      .getDepots(regiondId)
      .then((result) => {
        this.searchingDepots = false;
        var selectDepot = null;

        var items = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.name, value: x });
          });

          if (this.jobInfo.region != null && this.jobInfo.region.id == regiondId) {
            items.forEach((x) => {
              if (x.value != null && x.value.id == this.jobInfo.depot.id) {
                selectDepot = x.value;
              }
            });
          }
        }

        this.depots = items;
        this.selectedDepot = selectDepot;
      })
      .catch((x) => {
        this.searchingDepots = false;
        this.depots = [];
        this.selectedDepot = null;
        this.messageService.add(
          Message.Error('An error occurred while loading Depots, please try again.', 'Load Depots'),
        );
      });
  }

  CancelChangeSupplierPriceAccount() {
    this.isShowChangeSupplierPriceAccount = false;
  }

  SaveChangeSupplierPriceAccount() {
    this.isBusyChangeSupplierPriceAccount = true;
    var selectedTagIds = [];

    this.selectedJobTags.forEach((x) => {
      selectedTagIds.push(x.id);
    });

    this.jobService
      .changeSupplierPriceAccount({
        jobId: this.jobInfo.id,
        accountCodeId: this.selectedAccountCode != null ? this.selectedAccountCode.id : null,
        regionId: this.selectedRegion != null ? this.selectedRegion.id : null,
        depotId: this.selectedDepot != null ? this.selectedDepot.id : null,
        supplierPrice: this.supplierPrice,
        selectedTagIds: selectedTagIds,
      })
      .then((x) => {
        this.isBusyChangeSupplierPriceAccount = false;
        this.jobInfo.supplierPrice = this.supplierPrice;
        this.jobInfo.region = this.selectedRegion;
        this.jobInfo.depot = this.selectedDepot;
        this.jobInfo.accountCodeList = this.selectedAccountCode;
        this.jobInfo.jobTags = [];
        this.selectedJobTags.forEach((x) => {
          this.jobInfo.jobTags.push({
            id: 0,
            jobId: 0,
            orderTagId: x.id,
            description: x.description,
          });
        });
        this.messageService.add(
          Message.Success('Changes were successful.', 'Change Supplier Price/Account'),
        );
        this.isShowChangeSupplierPriceAccount = false;
      })
      .catch((x) => {
        this.isBusyChangeSupplierPriceAccount = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Supplier Price/Account',
          ),
        );
      });
  }

  showChangeSupplierDetails() {
    this.isShowChangeSupplierDetails = true;
    this.loadERegions();
    this.loadESuppliers(true);

    this.smsText = this.jobInfo.smsText;
  }

  public esuppliers: SelectItem[];
  public eregions: SelectItem[];
  public selectedERegions: number[] = [];
  public selectedESuppliers: number[] = [];
  public smsText: string;

  loadERegions() {
    this.eregions = [];
    this.selectedERegions = [];
    this.selectedESuppliers = [];
    this.eRegionService.getAll().then((res) => {
      res.forEach((x) => this.eregions.push({ label: x.name, value: x.id }));
    });
  }

  onERegionChange(event) {
    this.loadESuppliers(false);
  }

  loadESuppliers(isAll: boolean = false) {
    let taskTypeId = this.jobInfo.orders[0].tasks[0].taskTypeId;

    if (isAll) {
      this.supplierService.getAll(taskTypeId).then((result) => {
        this.esuppliers = [];
        result.forEach((x) => {
          this.esuppliers.push({ label: x.name, value: x.id });
        });
      });
    } else {
      this.supplierService.getByERegions(this.selectedERegions, taskTypeId).then((result) => {
        this.esuppliers = [];
        result.forEach((x) => {
          this.esuppliers.push({ label: x.name, value: x.id });
        });
      });
    }
  }

  loadSuppliers() {
    this.suppliers = [];
    this.selectedSupplier = null;
    this.searchingSuppliers = true;

    this.supplierService
      .getAll()
      .then((result) => {
        this.searchingSuppliers = false;
        var selectSupplier = null;

        var items = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.name, value: x });
          });

          if (this.jobInfo.region != null) {
            items.forEach((x) => {
              if (x.value != null && x.value.id == this.jobInfo.supplierId) {
                selectSupplier = x.value;
              }
            });
          }
        }

        this.suppliers = items;
        this.selectedSupplier = selectSupplier;
      })
      .catch((x) => {
        this.searchingSuppliers = false;
        this.suppliers = [];
        this.selectedSupplier = null;
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Suppliers, please try again.',
            'Load Suppliers',
          ),
        );
      });
  }

  CancelChangeSupplierDetails() {
    this.isShowChangeSupplierDetails = false;
  }

  SaveChangeSupplierDetails() {
    /*if ((this.jobInfo.supplier == null && this.selectedSupplier == null) ||
            (this.jobInfo.supplier != null && this.selectedSupplier != null && this.jobInfo.supplier.id == this.selectedSupplier.id)) {
            this.isShowChangeSupplierDetails = false;
            return;
        }*/

    this.isBusyChangeSupplierDetails = true;
    this.jobService
      .changeSupplierDetails({
        jobId: this.jobInfo.id,
        supplierIds: this.selectedESuppliers,
        eRegionIds: this.selectedERegions,
        smsText: this.smsText,
      })
      .then((x) => {
        this.isBusyChangeSupplierDetails = false;
        this.messageService.add(
          Message.Success('Suppliers directed to job.', 'Change Supplier Price/Account'),
        );

        this.directedSuppliers = [];
        this.getDirectedSuppliers();

        this.isShowChangeSupplierDetails = false;

        if (x && !x.isOk) {
          this.messageService.add(Message.Warning(x.message));
        }
      })
      .catch((x) => {
        this.isBusyChangeSupplierDetails = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Supplier Details',
          ),
        );
      });
  }

  setChangePickupDialog(value: boolean) {
    this.pickupContactEdit = value;
  }

  setChangeDropoffDialog(value: boolean) {
    this.dropoffContactEdit = value;
  }

  showChangeDriverTruck() {
    if (this.jobInfo.supplier == null) {
      return;
    }

    this.isShowChangeDriverTruck = true;
    this.loadDrivers(this.jobInfo.supplier.id);
    this.loadTrucks(this.jobInfo.supplier.id);
  }

  loadDrivers(supplierId) {
    this.drivers = [];
    this.selectedDriver = null;
    this.searchingDrivers = true;

    this.driverService
      .getAll(supplierId)
      .then((result) => {
        this.searchingDrivers = false;
        var selectedDriver = null;

        var items = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.nickName, value: x });
          });

          if (this.jobInfo.region != null) {
            items.forEach((x) => {
              if (
                x.value != null &&
                this.selectedTask.driver != null &&
                x.value.id == this.selectedTask.driver.id
              ) {
                selectedDriver = x.value;
              }
            });
          }
        }

        this.drivers = items;
        this.selectedDriver = selectedDriver;
      })
      .catch((x) => {
        this.searchingDrivers = false;
        this.drivers = [];
        this.selectedDriver = null;
        this.messageService.add(
          Message.Error('An error occurred while loading Drivers, please try again.'),
        );
      });
  }

  loadTrucks(supplierId) {
    this.trucks = [];
    this.selectedTruck = null;
    this.searchingTrucks = true;

    this.trucksService
      .getAll(supplierId)
      .then((result) => {
        this.searchingTrucks = false;
        var selectedTruck = null;

        var items = [];
        items.push({ label: 'Please Select', value: null });
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.model + '(' + x.registration + ')', value: x });
          });

          if (this.jobInfo.region != null) {
            items.forEach((x) => {
              if (
                x.value != null &&
                this.selectedTask.truck != null &&
                x.value.id == this.selectedTask.truck.id
              ) {
                selectedTruck = x.value;
              }
            });
          }
        }

        this.trucks = items;
        this.selectedTruck = selectedTruck;
      })
      .catch((x) => {
        this.searchingTrucks = false;
        this.trucks = [];
        this.selectedTruck = null;
        this.messageService.add(
          Message.Error('An error occurred while loading Trucks, please try again.', 'Load Trucks'),
        );
      });
  }

  private toDisplayWeightConfirmation(): boolean {
    return this.selectedTask.vehicle.kerbWeight >= 3500;
  }

  isElectric(): boolean {
    return this.selectedTask?.vehicle?.isElectric;
  }

  private async showConfirmationDialog(message: string, header: string): Promise<boolean> {
    return new Promise((resolve) => {
      this.confirmationService.confirm({
        key: 'allocate-driver',
        message,
        header,
        accept: () => resolve(true),
        reject: () => resolve(false),
      });
    });
  }

  async SaveChangeDriverTruck() {
    this.isBusyChangeDriverTruck = true;

    if (this.toDisplayWeightConfirmation() && this.selectedDriver != null) {
      const confirmed = await this.showConfirmationDialog(
        `Are you sure ${this.selectedDriver?.firstName} ${this.selectedDriver?.surname} has the capability to carry out HGV movements?`,
        'Confirmation',
      );

      if (!confirmed) {
        this.isBusyChangeDriverTruck = false;
        return;
      }
    }

    this.jobService
      .changeDriverTruck({
        taskId: this.selectedTask.id,
        driverId: this.selectedDriver != null ? this.selectedDriver.id : null,
        truckId: this.selectedTruck != null ? this.selectedTruck.id : null,
      })
      .then((x) => {
        this.isBusyChangeDriverTruck = false;
        this.selectedTask.driver = this.selectedDriver;
        this.selectedTask.truck = this.selectedTruck;
        this.jobInfo.depot = this.selectedDepot;
        this.jobInfo.accountCodeList = this.selectedAccountCode;
        this.messageService.add(Message.Success('Changes were successful.', 'Change Driver/Truck'));
        this.isShowChangeDriverTruck = false;
      })
      .catch((x) => {
        this.isBusyChangeDriverTruck = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Driver/Truck',
          ),
        );
      });
  }

  closeChangeDriverTruck() {
    this.isShowChangeDriverTruck = false;
  }

  isDriverEqual(driver): boolean {
    if (this.selectedTask == null) {
      return false;
    }

    if (driver == null && this.selectedTask.driver == null) {
      return true;
    }

    if (
      (driver == null && this.selectedTask.driver != null) ||
      (driver != null && this.selectedTask.driver == null)
    ) {
      return false;
    }

    return driver.id == this.selectedTask.driver.id;
  }

  isTruckEqual(truck): boolean {
    if (this.selectedTask == null) {
      return false;
    }

    if (truck == null && this.selectedTask.truck == null) {
      return true;
    }

    if (
      (truck == null && this.selectedTask.truck != null) ||
      (truck != null && this.selectedTask.truck == null)
    ) {
      return false;
    }

    return truck.id == this.selectedTask.truck.id;
  }

  ShowSendMessageToDriver() {
    this.isSendMessageToDriver = true;
    this.smsMessage = '';
  }

  closeSendMessageToDriver() {
    this.isSendMessageToDriver = false;
  }

  sendMessageToDriver() {
    if (!this.smsMessage || this.smsMessage.length == 0) {
      return;
    }
    this.isBusySendMessageToDriver = true;

    this.driverService
      .sendMessage(this.selectedTask.driver.id, this.smsMessage, this.selectedTask.id)
      .then((x) => {
        this.messageService.add(Message.Success('Message sent successfully', 'Success'));
        this.isBusySendMessageToDriver = false;
        this.isSendMessageToDriver = false;
        this.reloadDriverMessages(this.selectedTask.id);
      })
      .catch((ex) => {
        this.isBusySendMessageToDriver = false;
        this.messageService.add(
          Message.Error('An error occurred while trying to send the message', 'Error'),
        );
      });
  }

  reloadDriverMessages(taskId) {
    this.isReloadDriverMessages = true;

    this.taskService
      .getDriverMessages(taskId)
      .then((result) => {
        this.isReloadDriverMessages = false;
        this.selectedTask.driverMessages = result;
      })
      .catch((ex) => {
        this.isReloadDriverMessages = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while updating the data. The current data may not be correct.',
            'Reload Driver Messages',
          ),
        );
      });
  }

  showChangeJobDetails() {
    if (this.organisationTypeId != 1) {
      return;
    }

    this.isShowChangeJobDetails = true;

    if (typeof this.jobInfo.heldEndDate == 'string') {
      this.heldEndDate = new Date(this.jobInfo.heldEndDate);
    } else {
      this.heldEndDate = this.jobInfo.heldEndDate;
    }

    if (typeof this.jobInfo.exisingCapacityEndDate == 'string') {
      this.exisingCapacityEndDate = new Date(this.jobInfo.exisingCapacityEndDate);
    } else {
      this.exisingCapacityEndDate = this.jobInfo.exisingCapacityEndDate;
    }
    if (typeof this.jobInfo.scheduledDate == 'string') {
      this.scheduledDate = new Date(this.jobInfo.scheduledDate);
    } else {
      this.scheduledDate = this.jobInfo.scheduledDate;
    }

    this.buyItNowPrice = this.jobInfo.buyItNowPrice;
    this.existingCapacityPrice = this.jobInfo.existingCapacityPrice;
    this.supplierPrice = this.jobInfo.supplierPrice;
    this.insuranceCost = this.jobInfo.insuranceCost;
    this.insuranceProviderId = this.jobInfo.insuranceProviderId;

    this.jobDetailsForm.clearValidators();
  }

  cancelChangeJobDetails() {
    this.isShowChangeJobDetails = false;
  }

  changeJobDetailsValid() {
    return !(isNaN(this.buyItNowPrice) || isNaN(this.existingCapacityPrice));
  }

  additionalChargeValid() {
    return (
      !isNaN(this.additionalCharge.chargeAmount) &&
      this.additionalCharge.chargeAmount > 0 &&
      this.additionalCharge.chargeAmount <= 9999999.99
    );
  }

  saveContactDetails() {
    this.dropoffContactEdit = false;
    this.pickupContactEdit = false;

    this.taskService.SaveContactDetails({
      taskId: this.selectedTask.id,
      pickupForename: this.selectedTask.pickupForename,
      pickupSurname: this.selectedTask.pickupSurname,
      pickupEmail: this.selectedTask.pickupEmail,
      pickupSecondaryEmail: this.selectedTask.pickupSecondaryEmail,
      pickupPhone1: this.selectedTask.pickupPhone1,
      pickupPhone2: this.selectedTask.pickupPhone2,
      pickupNotification: this.selectedTask.pickupNotification,
      dropoffForename: this.selectedTask.dropoffForename,
      dropoffSurname: this.selectedTask.dropoffSurname,
      dropoffEmail: this.selectedTask.dropoffEmail,
      dropoffSecondaryEmail: this.selectedTask.dropoffSecondaryEmail,
      dropoffPhone1: this.selectedTask.dropoffPhone1,
      dropoffPhone2: this.selectedTask.dropoffPhone2,
      dropoffNotification: this.selectedTask.dropoffNotification,
    });
  }

  saveChangeJobDetails() {
    this.isBusyChangeJobDetails = true;

    if (this.heldEndDate) {
      this.heldEndDate.setSeconds(0);
      this.heldEndDate.setMilliseconds(0);
    }
    if (this.exisingCapacityEndDate) {
      this.exisingCapacityEndDate.setSeconds(0);
      this.exisingCapacityEndDate.setMilliseconds(0);
    }
    if (this.scheduledDate) {
      this.scheduledDate.setSeconds(0);
      this.scheduledDate.setMilliseconds(0);
    }

    this.jobService
      .changeJobDetails({
        jobId: this.jobInfo.id,
        heldEndDate: moment(this.heldEndDate).format('YYYY-MM-DD HH:mm'),
        exisingCapacityEndDate: moment(this.exisingCapacityEndDate).format('YYYY-MM-DD HH:mm'),
        scheduledDate: moment(this.scheduledDate).format('YYYY-MM-DD HH:mm'),

        buyItNowPrice: this.buyItNowPrice,
        existingCapacityPrice: this.existingCapacityPrice,
        supplierPrice: this.supplierPrice,
        insuranceCost: this.insuranceCost,
        insuranceProviderId: this.insuranceProviderId,
      })
      .then((x) => {
        this.isBusyChangeJobDetails = false;

        this.jobInfo.heldEndDate = this.heldEndDate;
        this.jobInfo.exisingCapacityEndDate = this.exisingCapacityEndDate;
        this.jobInfo.scheduledDate = this.scheduledDate;

        this.jobInfo.buyItNowPrice = Number(this.buyItNowPrice);
        this.jobInfo.existingCapacityPrice = Number(this.existingCapacityPrice);
        this.jobInfo.supplierPrice = Number(this.supplierPrice);
        this.jobInfo.insuranceCost = Number(this.insuranceCost);
        this.jobInfo.insuranceProviderId = Number(this.insuranceProviderId);

        if (this.insuranceProviderId) {
          var provider = this.insuranceProviders.find((x) => x.value == this.insuranceProviderId);
          this.jobInfo.insuranceProviderName = provider != null ? provider.label : '';
        }

        this.fillJobTimeline();
        this.isShowChangeJobDetails = false;
        this.getStatus();
        this.LoadJob();
      })
      .catch((x) => {
        this.isBusyChangeJobDetails = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Job Details',
          ),
        );
      });
  }

  noteTypes: any[];

  showAddDriverNotes() {
    this.isShowAddDriverNotes = true;

    this.noteTypes = [];
    this.noteTypes.push({ label: 'Pick-up', value: true });
    this.noteTypes.push({ label: 'Drop-off', value: false });
    this.newNoteDescription = '';
    this.newNoteIsPickup = true;
  }

  closeAddDriverNotes() {
    this.isShowAddDriverNotes = false;
  }

  saveAddDriverNotes() {
    if (!this.newNoteDescription || this.newNoteDescription.length == 0) {
      return;
    }

    this.isBusyAddDriverNotes = true;

    this.jobService
      .addDriverNotes({
        taskId: this.selectedTask.id,
        note: this.newNoteDescription,
        isPickup: this.newNoteIsPickup,
      })
      .then((x) => {
        this.isBusyAddDriverNotes = false;
        this.reloadNotes(this.selectedTask.id);
        this.messageService.add(Message.Success('Changes were successful.', 'Change Driver Notes'));
        this.isShowAddDriverNotes = false;
      })
      .catch((x) => {
        this.isBusyAddDriverNotes = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Change Driver Notes',
          ),
        );
      });
  }

  reloadNotes(taskId: number) {
    this.isReloadNotes = true;

    this.taskService
      .getNotes(taskId)
      .then((result) => {
        this.isReloadNotes = false;
        this.selectedTask.notes = result;
      })
      .catch((ex) => {
        this.isReloadNotes = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while updating the data. The current data may not be correct.',
            'Reload Notes',
          ),
        );
      });
  }

  copyOrder(selectedOrder) {
    if (!selectedOrder) {
      return;
    }

    //this.inboxBackService.setInbox(this.previousUrl, this.previousInboxType);

    this.router.navigate(['app/order/copy/' + selectedOrder.id]);
  }

  private setCancellationStatusAndSubStatus() {
    setTimeout(() => {
      if (this.selectedOrder.cancellationStatusId) {
        const statusId = Number(this.selectedOrder.cancellationStatusId);
        this.cancellationStatusId = statusId;
      }
      this.showSubCancellationStatusIfApplicable();
      this.secondaryCancellationStatusId = this.selectedOrder.secondaryCancellationStatusId;
    });
  }

  showEditOrderDialog(selectedOrder: IOrderScreen) {
    if (!selectedOrder) {
      return;
    }

    if (this.cancellationStatuses.length == 0) {
      this.initCancellationStatuses().then((x) => {
        this.setCancellationStatusAndSubStatus();
      });
    } else {
      this.setCancellationStatusAndSubStatus();
    }

    this.orderService.getFullCancellationDescription(selectedOrder.id).then((res) => {
      this.cancellationDescription = res.fullDescription;
    });

    this.orderCustomerPrice = selectedOrder.customerPrice;
    this.isOverridePrice = selectedOrder.isOverridePrice;
    this.purchaseOrderNo = selectedOrder.purchaseOrderNo;
    this.orderInspectionCharge = selectedOrder.inspectionCharge;

    this.selectedAccountCode = selectedOrder.accountCodeList;
    this.accountCodes = [];

    this.orderTags = [];
    this.selectedOrderTags = [];

    this.orderShowInspectionCharge = [
      TaskType.DrivenPlatedInspection,
      TaskType.DrivenInsuredInspection,
    ].includes(selectedOrder.taskTypeId);

    this.loadAccountCodes(selectedOrder.customerId, selectedOrder);

    this.loadOrderTags(selectedOrder.customerId, selectedOrder);

    //get selected order's department
    this.loadDepartments();

    this.isShowEditOrder = true;
  }

  loadDepartments() {
    this.orgService.getOrg(this.selectedOrder.customerId).then((org) => {
      var allDepartments = org.departments;
      this.customerDepartments = [];
      allDepartments.sort(this.compareItem);
      this.customerDepartments = allDepartments;
      this.selectedOrderDepartment = this.selectedOrder.customerDepartment;
      this.loadTeams(this.selectedOrder.customerDepartment.id);
    });
  }

  loadTeams(departmentId: number): Promise<any> {
    var promise = null;
    this.departmentTeams = [];
    if (departmentId != null) {
      promise = this.orgService.getDepartment(departmentId).then((data) => {
        if (data != null) {
          var allTeams = data.teams;
          allTeams.sort(this.compareItem);
          this.departmentTeams = allTeams;
          this.selectedOrderTeam = this.selectedOrder.customerTeam;
        }
      });
    }
    return promise;
  }

  departmentChange(event) {
    this.selectedOrderTeam = null;
    this.loadTeams(event.value.id);
  }

  compareItem(x, y) {
    if (x.name.toLowerCase() > y.name.toLowerCase()) return 1;
    else if (x < y) return -1;
    else return 0;
  }

  loadOrderTags(supplierId, selectedOrder: IOrderScreen) {
    if (supplierId == null || selectedOrder == null) {
      return;
    }

    this.searchingTags = true;
    this.orderTagService
      .getForCustomer(supplierId)
      .then((result) => {
        this.searchingTags = false;
        var selectedOrderTags = [];
        var items = [];
        if (result != null) {
          result.forEach((x) => {
            items.push({ label: x.description, value: x });
          });

          if (selectedOrder.orderTags != null) {
            items.forEach((x) => {
              selectedOrder.orderTags.forEach((y) => {
                if (x.value != null && x.value.id == y.orderTagId) {
                  selectedOrderTags.push(x.value);
                }
              });
            });
          }
        }

        this.orderTags = items;
        this.selectedOrderTags = selectedOrderTags;
      })
      .catch((x) => {
        this.searchingTags = false;
        this.orderTags = [];
        this.selectedOrderTags = [];
        this.messageService.add(
          Message.Error(
            'An error occurred while loading Job Tags, please try again.',
            'Load Job Tags',
          ),
        );
      });
  }

  closeEditOrder() {
    this.isShowEditOrder = false;
    this.cancellationStatusId = null;
    this.secondaryCancellationStatusId = null;
    this.toDisplaySecondaryCancellation = false;
  }

  saveEditOrder() {
    this.isBusyEditOrder = true;
    var selectedTagIds = [];

    this.selectedOrderTags.forEach((x) => {
      selectedTagIds.push(x.id);
    });

    this.jobService
      .changeOrderDetail({
        orderId: this.selectedOrder.id,
        accountCodeId: this.selectedAccountCode != null ? this.selectedAccountCode.id : null,
        customerPrice: this.orderCustomerPrice,
        selectedTagIds: selectedTagIds,
        isOverridePrice: this.isOverridePrice,
        purchaseOrderNo: this.purchaseOrderNo,
        cancellationStatusId: this.cancellationStatusId,
        cancellationDescription: this.cancellationDescription,
        customerDepartmentId: this.selectedOrderDepartment?.id,
        customerTeamId: this.selectedOrderTeam?.id,
        secondaryCancellationStatusId: this.secondaryCancellationStatusId,
        inspectionCharge: this.orderInspectionCharge,
      })
      .then((x) => {
        this.isBusyEditOrder = false;
        this.selectedOrder.secondaryCancellationStatusId = this.secondaryCancellationStatusId;
        this.selectedOrder.cancellationDescription =
          this.cancellationDescription && this.cancellationDescription.length > 15
            ? this.cancellationDescription.substring(0, 15) + '...'
            : this.cancellationDescription;
        this.selectedOrder.cancellationStatusId = this.cancellationStatusId;
        var status = this.cancellationStatuses.find((x) => x.value == this.cancellationStatusId);
        if (status != null) {
          this.selectedOrder.cancellationStatusName = status.label;
        }

        this.selectedOrder.customerPrice = this.orderCustomerPrice;
        this.selectedOrder.isOverridePrice = this.isOverridePrice;
        this.selectedOrder.purchaseOrderNo = this.purchaseOrderNo;
        this.selectedOrder.accountCodeList = this.selectedAccountCode;
        this.selectedOrder.customerTeam = this.selectedOrderTeam;
        this.selectedOrder.customerDepartment = this.selectedOrderDepartment;
        this.selectedOrder.inspectionCharge = this.orderInspectionCharge;
        this.selectedOrder.orderTags = [];
        this.selectedOrderTags.forEach((x) => {
          this.selectedOrder.orderTags.push({ orderTagId: x.id, description: x.description });
        });
        this.messageService.add(Message.Success('Changes were successful.', 'Edit order'));
        this.isShowEditOrder = false;
        //update the current job info list
        let currIndex = this.jobInfo.orders.findIndex((t) => t.id == this.selectedOrder.id);
        if (currIndex > -1) {
          this.jobInfo.orders[currIndex] = this.selectedOrder;
        }
        this.toDisplaySecondaryCancellation = false;
      })
      .catch((x) => {
        this.isBusyEditOrder = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while trying to modify the data. Try again. If the error persists, contact your administrator.',
            'Edit order',
          ),
        );
      });
  }

  /*Photo upload*/
  public isBusyAddPhotoToStep: boolean = false;
  public isBusyUploadImage: boolean = false;
  public selectedImage: any = null;
  public selectedImageSource: string = null;
  public odometerImageSource: string = null;

  public selectedCompleteTaskStep: ITaskStep = null;

  public areas: any[] = [];
  public selectedArea: any = null;

  public addPhotoDescription: string = null;

  public multipleFileUploadResult: IUploadResultDTO[] = [];

  canAddPhoto(type: StepType) {
    if (this.organisationTypeId != 1 && this.organisationTypeId != 3) {
      return false;
    }

    let images: IGalleryImage[] = [];
    if (type == 1) {
      images = this.pickupImages;
    } else if (type == 3) {
      images = this.dropoffImages;
    }

    return images.length > 0 || this.selectedTask.completeTaskSteps.some((t) => t.stepType == type);
  }

  showAddPhotoToTaskStep(type: StepType) {
    this.isShowAddPhotoToTaskStep = true;

    this.selectedCompleteTaskStep = null;
    this.selectedImage = null;
    this.addPhotoDescription = null;

    let steps = this.selectedTask.completeTaskSteps.filter((x) => x.stepType == type);
    if (steps.length) {
      this.selectedCompleteTaskStep = steps[0];
      this.initAreas(this.selectedCompleteTaskStep);
      if (this.areas.length) {
        this.selectedArea = this.areas[0].value;
      }
    }
    this.initFirstAreaCondition(type);
  }

  initAreas(step: ITaskStep) {
    this.selectedArea = null;
    this.areas = [];
    for (var i = 0; i < 7; i++) {
      this.areas.push({ label: this.getAreaTitle(i), value: i });
    }

    this.selectedTask.requiredPhotoAreas.forEach((x) => {
      this.areas.push({ label: this.getAreaTitle(x), value: x });
    });
  }

  closeAddPhotoToTaskStep() {
    this.isShowAddPhotoToTaskStep = false;
    this.isShowEditPhotoToTaskStep = false;
    this.multipleFileUploadResult = [];
  }

  savePhotoOnStep() {
    if (this.isShowAddPhotoToTaskStep) this.addPhotoToStep();
    else this.updatePhotoToStep();
  }

  savePhotoOnOrgSpecificStep() {
    this.showChecklistStepPhotosDialog = false;

    if (this.selectedImage == null) return;

    if (this.currentTaskStep == null) return;

    this.isBusyAddPhotoToStep = true;

    // Create object for display
    this.currentTaskStep.stepPhotos.push({
      mediaId: this.selectedImage.mediaId,
      photoLocation: this.selectedImage.location,
      dateTime: new Date(),
    });

    // Prepare object for API (it expects customStepPhotos instead of stepPhotos)
    this.currentTaskStep.customStepPhotos = this.currentTaskStep.stepPhotos;

    return this.saveEdit(this.currentTaskStep).then((x) => {});
  }

  savePhotoOnOrgSpecificStepAnswer() {
    this.showChecklistStepAnswerPhotosDialog = false;

    if (this.selectedImage == null) return;

    if (this.currentTaskStepAnswer == null) return;

    if (this.currentTaskStep == null) return;

    this.currentTaskStep.answers
      .filter((x) => x.id == this.currentTaskStepAnswer.id)
      .map((x) => {
        x.answerMedia.push({
          mediaId: this.selectedImage.mediaId,
          photoLocation: this.selectedImage.location,
        });
      });

    return this.saveEdit(this.currentTaskStep);
  }

  addPhotoToStep() {
    if (
      this.selectedArea == null ||
      this.selectedCompleteTaskStep == null ||
      this.selectedImage == null
    )
      return;

    this.isBusyAddPhotoToStep = true;

    this.taskService
      .AddPhotoToTaskStep({
        taskId: this.selectedTask.id,
        taskStepId: this.selectedCompleteTaskStep.id,
        photoArea: this.selectedArea,
        photoLocation: this.selectedImage.fullName,
        description: this.addPhotoDescription,
      })
      .then((result) => {
        this.isBusyAddPhotoToStep = false;
        this.isShowAddPhotoToTaskStep = false;
        this.isShowEditPhotoToTaskStep = false;

        this.selectedCompleteTaskStep.photos.push({
          photoArea: this.selectedArea,
          dateTime: result.dateTime,
          description: this.addPhotoDescription,
          photoLocation: result.photoLocation,
          taskStepId: this.selectedCompleteTaskStep.id,
          id: result.id,
          extension: result.extension,
        });

        var area = this.selectedCompleteTaskStep.areas.find(
          (x) => x.photoArea == this.selectedArea,
        );

        if (!area) {
          this.selectedCondition = 0; // we have been asked to set it to Good by default.
          this.setAreacCondition();
        } else {
          this.updateImages(this.selectedTask, this.selectedCompleteTaskStep.stepType, false);

          this.selectedArea = null;
          this.selectedCompleteTaskStep = null;
          this.selectedImage = null;
          this.addPhotoDescription = null;
        }

        this.messageService.add(Message.Success('Photo successfully added.', 'Add Photo To Step'));
      })
      .catch((ex) => {
        this.isBusyAddPhotoToStep = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while adding the photo to task. Try again.',
            'Add Photo To Step',
          ),
        );
      });
  }

  saveMultiplePhotosOnStep() {
    if (this.isShowAddPhotoToTaskStep) this.addMultiplePhotosToStep();
    else this.updatePhotoToStep();

    this.setAreacCondition();
  }

  public taskStepMultiPhotoInfo: ITaskStepPhotoInfo[] = [];

  addMultiplePhotosToStep() {
    if (
      this.selectedArea == null ||
      this.selectedCompleteTaskStep == null ||
      this.multipleFileUploadResult.length <= 0
    )
      return;

    this.isBusyAddPhotoToStep = true;

    for (var i = 0; i < this.multipleFileUploadResult.length; i++) {
      let photoInfo = {} as ITaskStepPhotoInfo;
      photoInfo.taskId = this.selectedTask.id;
      photoInfo.taskStepId = this.selectedCompleteTaskStep.id;
      photoInfo.photoArea = this.multipleFileUploadResult[i].area;
      photoInfo.photoLocation = this.multipleFileUploadResult[i].fileData.guid;
      photoInfo.description = this.multipleFileUploadResult[i].description;
      this.taskStepMultiPhotoInfo.push(photoInfo);
    }

    this.taskService
      .AddMultiplePhotosToTaskStep(this.taskStepMultiPhotoInfo)
      .then((result) => {
        this.isBusyAddPhotoToStep = false;
        this.isShowAddPhotoToTaskStep = false;
        this.isShowEditPhotoToTaskStep = false;

        for (var i = 0; i < result.length; i++) {
          this.selectedCompleteTaskStep.photos.push({
            photoArea: result[i].photoTaskStep.photoArea,
            dateTime: result[i].photoTaskStep.dateTime,
            description: result[i].photoTaskStep.description,
            photoLocation: result[i].photoTaskStep.photoLocation,
            taskStepId: this.selectedCompleteTaskStep.id,
            id: result[i].photoTaskStep.id,
            extension: result[i].photoTaskStep.extension,
          });

          var area = this.selectedCompleteTaskStep.areas.find(
            (x) => x.photoArea == result[i].photoTaskStep.photoArea,
          );
          if (!area) {
            this.selectedCompleteTaskStep.areas.push({
              id: 0,
              condition: 0, // we have been asked to set it to Good by default.
              photoArea: result[i].photoTaskStep.photoArea,
              taskStepId: this.selectedCompleteTaskStep.id,
              dateTime: new Date(),
              comment: null,
            });
          }
        }
        this.updateImages(this.selectedTask, this.selectedCompleteTaskStep.stepType, false);

        this.selectedArea = null;
        this.multipleFileUploadResult = [];
        this.taskStepMultiPhotoInfo = [];
        this.addPhotoDescription = null;

        this.messageService.add(Message.Success('Photo successfully added.', 'Add Photo To Step'));
      })
      .catch((ex) => {
        this.isBusyAddPhotoToStep = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while adding the photo to task. Try again.',
            'Add Photo To Step',
          ),
        );
      });
  }

  progress: number = 0;
  showProgressBar: boolean = false;
  files: any;

  onBeforeUpload(event) {
    this.isBusyAddPhotoToStep = true;
    var lmDate = this.files[0].LastModifiedDate;
    if (!lmDate) lmDate = new Date();

    event.formData.append('lastModifiedDate', lmDate.getTime());
    event.formData.append('size', '400');
    event.formData.append(
      'area',
      this.selectedArea || this.selectedArea == '0' ? this.selectedArea : '6',
    );
    event.formData.append('description', this.addPhotoDescription ? this.addPhotoDescription : '');
    event.formData.append('jobId', this.jobId);
    event.formData.append('taskId', this.selectedTask.id);
  }

  onError(event) {
    var errorDescripton = 'The following files were not be uploaded: <br/>';
    if (event.files.length > 0) {
      for (let file of event.files) {
        errorDescripton += file.name + '<br/>';
      }
    }
    this.isBusyAddPhotoToStep = false;

    this.messageService.add(
      Message.Error(
        'An error occurred while uploading files.Please try again.<br />' + errorDescripton,
        'File Uploading',
      ),
    );
  }

  progressUploading(event) {
    this.progress = event.progress;
  }

  onSelect(event) {
    this.files = [];
    if (event.files.length > 0) {
      for (let file of event.files) {
        var fileDetails = {
          LastModifiedDate: file.lastModifiedDate,
          Name: file.name,
          Size: file.size,
          Type: file.type,
        };
        this.files.push(fileDetails);
      }
    }
  }

  onUpload(event) {
    let response = event.originalEvent.body;

    if (response.successfully) {
      this.selectedImageSource = response.presignedUrl;
      this.selectedImage = {
        alt: '',
        mediaId: response.fileData.mediaId,
        title: response.fileData.name,
        name: response.fileData.name,
        size: Math.round(response.fileData.size / 1024),
        lastModifiedDate: response.fileData.lastModifiedDate,
        guid: response.fileData.guid,
        fullName: response.fileData.guid,
        extension: response.fileData.extension,
        location: response.presignedUrl,
      };
    } else {
      this.selectedImage = null;
      this.selectedImageSource = null;
      this.messageService.add(Message.Error(response.errorMessage, 'File Uploading'));
    }
    this.isBusyAddPhotoToStep = false;
  }

  onMultipleFileUpload(event) {
    let response = event.originalEvent.body;
    if (response) {
      for (var i = 0; i < response.length; i++) {
        this.multipleFileUploadResult.push(response[i]);
      }
    }
    this.isBusyAddPhotoToStep = false;
  }

  fileUploading(event) {
    var totalUploadedFiles = 0;
    var totalFiles = event.files.length;
    this.progress = 0;

    if (event.files != undefined && event.files.length > 0) {
      this.showProgressBar = true;
      for (let file of event.files) {
        let input = new FormData();
        input.append('file', file);

        this.http
          .post(
            '/api/filestorage/fileuploading?lastModifiedDate=' +
              file.lastModifiedDate.getTime() +
              '&size=400',
            input,
          )
          .toPromise()
          .then((d: any) => {
            var uploadingResult = d;
            if (uploadingResult.isSuccessStatusCode) {
              totalUploadedFiles++;
              this.progress = Math.round((totalUploadedFiles * 100) / totalFiles);
              if (totalUploadedFiles == totalFiles) {
                this.messageService.add(
                  Message.Success('The files have been uploaded successfully.', 'File Uploading'),
                );
              }
            } else {
              this.messageService.add(
                Message.Warning(uploadingResult.reasonPhrase, 'File Uploading'),
              );
              this.showProgressBar = false;
              return;
            }
          });
      }
    }
  }

  /* set area condtion */

  isBusySetAreaCondition: boolean = false;
  isShowSetAreaCondition: boolean = false;
  conditions = [
    { label: this.getConditionTitle(0), value: 0 },
    { label: this.getConditionTitle(1), value: 1 },
    { label: this.getConditionTitle(2), value: 2 },
    { label: this.getConditionTitle(3), value: 3 },
  ];
  selectedCondition = null;
  conditionComment: string = null;

  isConditionCommentValid() {
    if (
      this.selectedCondition == PhotoAreaCondition.Good ||
      this.selectedCondition == PhotoAreaCondition.NotRequired
    ) {
      return true;
    } else {
      return this.conditionComment != null && this.conditionComment != '';
    }
  }

  showSetAreaCondition(event: any) {
    let type: StepType = event.type;
    this.selectedImage = event.image;

    this.isShowSetAreaCondition = true;

    this.initAreaCondition(type);
  }

  initAreaCondition(type: StepType) {
    this.selectedCompleteTaskStep = null;
    this.areas = [];
    this.selectedArea = null;
    this.selectedCondition = null;
    this.conditionComment = null;

    let steps = this.selectedTask.completeTaskSteps.filter((x) => x.stepType == type);
    if (steps.length) {
      let step = steps[0];
      this.selectedCompleteTaskStep = step;
      this.initAreas(step);
      if (this.areas.length) {
        this.selectedArea = this.selectedImage
          ? this.areas.map((a) => a.value).find((a) => a == this.selectedImage.area)
          : this.areas[0].value;

        this.onAreaChange();
      }
    }
    this.selectedCondition = this.selectedImage ? this.selectedImage.condition : 0;
    this.refreshConditionComment();
  }

  initFirstAreaCondition(type: StepType) {
    this.selectedCompleteTaskStep = null;
    this.areas = [];
    this.selectedArea = null;
    this.selectedCondition = null;
    this.conditionComment = null;

    let steps = this.selectedTask.completeTaskSteps.filter((x) => x.stepType == type);
    if (steps.length) {
      let step = steps[0];
      this.selectedCompleteTaskStep = step;
      this.initAreas(step);
      if (this.areas.length) {
        this.selectedArea = this.areas[0].value;

        this.onAreaChange();
      }
    }

    this.refreshConditionComment();
  }

  closeSetAreaCondition() {
    this.isShowSetAreaCondition = false;
  }

  refreshConditionComment() {
    this.conditionComment = null;
    var area = this.selectedCompleteTaskStep.areas.find(
      (x) => x.photoArea == this.selectedArea && x.condition == this.selectedCondition,
    );
    if (area) {
      this.conditionComment = area.comment;
    }
  }

  onAreaChange() {
    this.addPhotoDescription = null;
    var area = this.selectedCompleteTaskStep.areas.find((x) => x.photoArea == this.selectedArea);

    if (this.selectedArea == PhotoArea.FuelGaugeOdometer) {
      // Gauge
      this.conditions = [{ label: this.getConditionTitle(4), value: 4 }];
      this.selectedCondition = PhotoAreaCondition.NotRequired;
    } else if (this.selectedArea == PhotoArea.Additional) {
      // Additional
      this.conditions = [
        { label: this.getConditionTitle(0), value: 0 },
        { label: this.getConditionTitle(1), value: 1 },
        { label: this.getConditionTitle(2), value: 2 },
        { label: this.getConditionTitle(3), value: 3 },
        { label: this.getConditionTitle(4), value: 4 },
      ];

      this.selectedCondition = area ? area.condition : PhotoAreaCondition.Good;
    } else {
      this.conditions = [
        { label: this.getConditionTitle(0), value: 0 },
        { label: this.getConditionTitle(1), value: 1 },
        { label: this.getConditionTitle(2), value: 2 },
        { label: this.getConditionTitle(3), value: 3 },
      ];

      this.selectedCondition = area ? area.condition : PhotoAreaCondition.Good;
    }
    this.refreshConditionComment();
  }

  onConditionChange(event) {
    this.refreshConditionComment();
  }

  setAreacCondition() {
    if (this.selectedArea == null || this.selectedCondition == null) {
      return;
    }

    this.isBusySetAreaCondition = true;

    this.taskService
      .setAreaCondition({
        taskId: this.selectedTask.id,
        taskStepId: this.selectedCompleteTaskStep.id,
        photoArea: this.selectedArea,
        condition: this.selectedCondition,
        comment: this.conditionComment,
      })
      .then(() => {
        this.isBusySetAreaCondition = false;
        this.isShowSetAreaCondition = false;

        var area = this.selectedCompleteTaskStep.areas.find(
          (x) => x.photoArea == this.selectedArea,
        );
        if (area) {
          area.condition = this.selectedCondition;
          area.comment = this.conditionComment;
        } else {
          this.selectedCompleteTaskStep.areas.push({
            id: 0,
            condition: this.selectedCondition,
            photoArea: this.selectedArea,
            taskStepId: this.selectedCompleteTaskStep.id,
            dateTime: new Date(),
            comment: this.conditionComment,
          });
        }

        this.updateImages(this.selectedTask, this.selectedCompleteTaskStep.stepType, false);

        this.selectedArea = null;
        this.selectedCondition = null;

        this.messageService.add(
          Message.Success('Condition successfully changed.', 'Set Area Condition'),
        );
      })
      .catch((ex) => {
        this.isBusySetAreaCondition = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while changing the condition for area. Try again.',
            'Set Area Condition',
          ),
        );
      });
  }

  /* end - set area condition */

  currentDate() {
    return new Date();
  }

  removeFromSupplier() {
    this.jobUnassignedReasonService.getReasons({ isActive: true }).then((x) => {
      this.unassignReasons = x.map((y, z) => {
        return { label: y.description, value: y.id };
      });

      this.showRemoveConfirm = true;
    });
  }

  confirmRemoveFromSupplier() {
    this.showRemoveConfirm = false;

    let removeSupplierInfo: IRemoveSupplier = {
      jobNumber: this.jobInfo.jobNumber,
      supplierId: this.jobInfo.supplierId,
      id: this.jobInfo.id,
      jobUnassignedComment: this.unassignComment,
      jobUnassignedReasonId: this.unassignReasonId,
    };

    this.jobService.removeJobSupplier(removeSupplierInfo).then((x) => {
      // refresh incidents if one has been raised as a result of unassigning supplier
      if (x) {
        this.eventService.RefreshIncidentsEvent.emit();
      }
      //Refresh the page as we are resetting alot of data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    });
  }

  onDriverChecklistChanged(rowData) {
    var toSend = <any>{
      taskStepId: rowData.id,
    };

    toSend.isCustomStep = rowData.isCustomStep;
    toSend.isOrganisationStep = rowData.isOrganisationStep;
    toSend.jobId = this.jobInfo.id;
    toSend.taskId = this.selectedTask.id;
    toSend.isComplete = rowData.isComplete;

    if (rowData.date) {
      toSend.date = rowData.date;
    }

    if (
      !rowData.isComplete &&
      (rowData.stepType == StepType.StartTask || // Heading to Pick-up Location
        rowData.stepType == StepType.PickupPhoto || // Pick-up Photos
        rowData.stepType == StepType.PickupSignature || // Pick-up Signature
        rowData.stepType == StepType.DropOffPhoto || // Drop-off Photos
        rowData.stepType == StepType.DropOffSignature || // Drop-off Signature
        rowData.stepType == StepType.StartMovement || // Start Movement
        rowData.stepType == StepType.Rating) // Customer Rating
    ) {
      this.confirmationService.confirm({
        message: `Un-ticking this step will result in the loss of data from the task. Do you want to continue?`,
        acceptLabel: 'Continue',
        rejectLabel: 'Cancel',
        accept: () => {
          this._completeTaskStep(rowData, toSend);
        },
        reject: () => {
          rowData.isComplete = true;
        },
      });
    } else {
      this._completeTaskStep(rowData, toSend);
    }
  }

  private _completeTaskStep(rowData, toSend) {
    this.taskService
      .completeTaskStep(toSend)
      .then((res) => {
        if (res) {
          if (!rowData.date) {
            rowData.date = new Date(Date.now());
          }

          if (!rowData.isComplete) {
            rowData.date = null;
          }

          var step;
          if (rowData.isTaskstep) {
            step = this.selectedTask.taskSteps.find((x) => x.id == rowData.id);
          } else if (rowData.isCustomStep) {
            step = this.selectedTask.customTaskActions.find((x) => x.id == rowData.id);
          } else if (rowData.isOrganisationStep) {
            step = this.selectedTask.taskOrganisationSpecificSteps.find(
              (x) => x.organisationSpecificStepId == rowData.id,
            );
          }

          if (step) {
            step.completedDateTime = rowData.date;
          }

          this.taskService.getCompletedTaskSteps(this.selectedTask.id).then((x) => {
            this.selectedTask.completeTaskSteps = x;
            this.updateSelectedTaskSteps();
          });
        }
      })
      .catch((ex) => {
        rowData.isComplete = !rowData.isComplete;
      });
  }

  setAdditionalCostChargeType() {
    this.isWaitingTimeCharge =
      this.additionalCharge.additionalChargeTypeId ==
        this.AdditionalChargeType.WaitingTimeDropOffAwaitingCustomerSignOff ||
      this.additionalCharge.additionalChargeTypeId ==
        this.AdditionalChargeType.WaitingTimeDropOffDelayAtSite ||
      this.additionalCharge.additionalChargeTypeId ==
        this.AdditionalChargeType.WaitingTimeDuringMove ||
      this.additionalCharge.additionalChargeTypeId ==
        this.AdditionalChargeType.WaitingTimePickUpAwaitingCustomerSignOff ||
      this.additionalCharge.additionalChargeTypeId ==
        this.AdditionalChargeType.WaitingTimePickUpDelayAtSite;

    this.isChargingTimeCharge =
      this.additionalCharge.additionalChargeTypeId == this.AdditionalChargeType.EVChargingCost ||
      this.additionalCharge.additionalChargeTypeId == this.AdditionalChargeType.EVChargingTime;
  }

  updateIncidentSection() {
    this.eventService.RefreshIncidentsEvent.emit();
  }

  getStartTaskDate(task: ITaskInfo): Date {
    if (task.completeTaskSteps.length > 0) {
      const completedDateStep = task.completeTaskSteps.find((x) => x.completedDateTime != null);
      if (completedDateStep) {
        return completedDateStep.completedDateTime;
      }
      return null;
    }
    return null;
  }

  cancellationDescription: string;
  cancellationStatusId: number;
  secondaryCancellationStatusId: number;
  public cancellationStatuses: any[] = [];
  allCancellationStatuses: ICancellationStatus[] = [];
  secondaryCancellationStatuses: SelectItem[] = [];
  allSecondaryCancellationStatuses: ISecondaryCancellationStatus[] = [];

  initCancellationStatuses() {
    this.cancellationStatuses = [];
    return this.orderService.getCancellationStatuses().then((res) => {
      this.allCancellationStatuses = res;
      res.forEach((x) => {
        this.cancellationStatuses.push({ label: x.name, value: x.id, tag: x });
      });
    });
  }

  showSubCancellationStatusIfApplicable() {
    const cancellationStatus = this.findCancellationStatusById(this.cancellationStatusId);

    if (this.shouldDisplaySecondaryCancellation(cancellationStatus)) {
      this.displaySecondaryCancellation(cancellationStatus);
    } else {
      this.hideSecondaryCancellation();
    }
  }

  private findCancellationStatusById(id) {
    return this.allCancellationStatuses.find((status) => status.id === id);
  }

  private shouldDisplaySecondaryCancellation(cancellationStatus) {
    return (
      cancellationStatus &&
      cancellationStatus.secondaryCancellationStatuses &&
      cancellationStatus.secondaryCancellationStatuses.length > 0
    );
  }

  private displaySecondaryCancellation(cancellationStatus) {
    this.toDisplaySecondaryCancellation = true;
    this.secondaryCancellationStatuses = [];
    this.allSecondaryCancellationStatuses = cancellationStatus.secondaryCancellationStatuses.filter(
      (status) => status.isActive,
    );

    this.secondaryCancellationStatuses.push({
      label: 'Please select secondary reason',
      value: 0,
    });

    const filteredSubStatuses = this.allSecondaryCancellationStatuses.map((status) => ({
      label: status.name,
      value: status.id,
    }));

    this.secondaryCancellationStatuses.push(...filteredSubStatuses);

    this.secondaryCancellationStatuses = this.secondaryCancellationStatuses.sort((a, b) => {
      return a.value - b.value;
    });
  }

  hasSelectedSecondaryStatus() {
    return this.allSecondaryCancellationStatuses.length === 0 || this.secondaryCancellationStatusId;
  }

  private hideSecondaryCancellation() {
    this.toDisplaySecondaryCancellation = false;
    this.toDisplaySecondaryCancellationStatusDescription = false;
  }

  onSecondaryCancellationStatusChange() {
    const secondaryStatus = this.allSecondaryCancellationStatuses.find(
      (x) => x.id === this.secondaryCancellationStatusId,
    );
    if (secondaryStatus.showDescription) {
      this.toDisplaySecondaryCancellationStatusDescription = true;
    } else {
      this.toDisplaySecondaryCancellationStatusDescription = false;
    }
  }

  cancelOrder() {
    this.cancellationDescription = null;
    //Customer
    if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      this.cancellationStatusId = CancellationStatus.CustomerCancellation;
    } else if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
      this.initCancellationStatuses();
    }

    this.orderService
      .getCancelOrderInfo(this.orderId ? this.orderId : this.selectedOrder.id)
      .then((res) => {
        this.customerChargedPrice = parseFloat(res.customerChargedPrice.toFixed(2));
        this.newCustomerChargedPrice = parseFloat(res.customerChargedPrice.toFixed(2));

        this.supplierChargedPrice = parseFloat(res.supplierChargedPrice.toFixed(2));
        this.newSupplierChargedPrice = parseFloat(res.supplierChargedPrice.toFixed(2));

        if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
          this.showCancelOrderVmeDialog = true;
        } else if (
          res.cancelOrderAction != this.cancelOrderAction_None ||
          this.organisationTypeId == OrganisationTypeEnum.Customer
        ) {
          this.showCancelOrderDialog = true;
        }
      });
  }

  cancelOrderConfirm(isFee: boolean = true) {
    var data: ICancelData = {
      isFee: isFee,
      customerChargedPrice: this.customerChargedPrice,
      newCustomerChargedPrice:
        this.newCustomerChargedPrice.toString().trim() != '' ? this.newCustomerChargedPrice : 0,
      supplierChargedPrice: this.supplierChargedPrice,
      newSupplierChargedPrice:
        this.newSupplierChargedPrice.toString().trim() != '' ? this.newSupplierChargedPrice : 0,
      jobId: this.jobId,
      orderId: this.orderId ? this.orderId : this.selectedOrder.id,
      cancellationStatusId: this.cancellationStatusId,
      cancellationDescription: this.cancellationDescription,
      secondaryCancellationStatusId: this.secondaryCancellationStatusId,
      secondaryCancellationDescription: this.secondaryCancellationDescription,
    };

    this.orderService
      .cancelOrder(data)
      .then(() => {
        if (this.cancelAction == this.cancelOrderAction_CustomerHoursAtWhichCap1Charged)
          this.messageService.add(Message.Success('Order is aborted', 'Abort Order'));
        else this.messageService.add(Message.Success('Order is cancelled', 'Cancel Order'));
        this.cancelAction = this.cancelOrderAction_None; //to disable aborting and hide the button
        this.Initialize().then(() => {});
      })
      .catch(() => {
        if (this.cancelAction == this.cancelOrderAction_AbortFee)
          this.messageService.add(Message.Success('Order aborting error', 'Abort Order'));
        else this.messageService.add(Message.Error('Cancel Order', 'Order cancelling error'));
      });

    this.showCancelOrderDialog = false;
  }

  cancelJob() {
    this.cancellationDescription = null;
    this.cancellationStatusId = CancellationStatus.SupplierCancellation;

    this.orderService.getCancelJobAction(this.jobId).then((res) => {
      this.cancelAction = res;

      if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
        //this.showCancelJobVmeDialog = true;
        this.showCancelJobDialog = true;
      } else if (res != this.cancelJobAction_None) {
        this.showCancelJobDialog = true;
      }
    });
  }

  cancelJobConfirm() {
    var data: ICancelData = {
      jobId: this.jobId,
      orderId: this.orderId ? this.orderId : this.selectedOrder.id,
      cancellationStatusId: this.cancellationStatusId,
      cancellationDescription: this.cancellationDescription,
      secondaryCancellationStatusId: this.secondaryCancellationStatusId,
      secondaryCancellationDescription: this.secondaryCancellationDescription,
    };

    this.orderService
      .cancelJob(data)
      .then(() => {
        this.messageService.add(Message.Success('Job is cancelled', 'Cancel Job'));
        this.cancelAction = this.cancelJobAction_None; //to disable aborting and hide the button
        this.Initialize();
      })
      .catch(() => {
        this.messageService.add(Message.Error('Job cancelling error', 'Cancel Job'));
      });

    this.showCancelJobDialog = false;
  }

  closeCancelJobOrder() {
    this.showCancelJobVmeDialog = false;
    this.showCancelOrderVmeDialog = false;
    this.secondaryCancellationStatusId = null;
    this.secondaryCancellationDescription = null;
    this.toDisplaySecondaryCancellation = false;
  }

  cancelJobOrder(isFee: boolean) {
    if (this.showCancelOrderVmeDialog) {
      this.cancelOrderConfirm(isFee);
    }
    if (this.showCancelJobVmeDialog) {
      this.cancelJobConfirm();
    }

    this.closeCancelJobOrder();
  }

  searchForTheJob() {
    this.router.navigate([
      '/app/adhocsearch',
      {
        pickupLat: this.selectedTask.pickupLocation.latitude,
        pickupLon: this.selectedTask.pickupLocation.longitude,
        dropoffLat: this.selectedTask.dropoffLocation.latitude,
        dropoffLon: this.selectedTask.dropoffLocation.longitude,
      },
    ]);
  }

  /**/

  allowServiceTypeEdit() {
    return (
      this.jobInfo.statusId == this.jobStatus_Created ||
      this.jobInfo.statusId == this.jobStatus_Completed ||
      this.jobInfo.statusId == this.jobStatus_Closed
    );
  }

  isJobCompleted(job: IJobScreen): boolean {
    if (!job) {
      return false;
    }

    return (
      job.statusId == this.jobStatus_Completed ||
      job.statusId == this.jobStatus_Cancelled ||
      job.statusId == this.jobStatus_Closed
    );
  }

  isJobCancelled(job: IJobScreen): boolean {
    if (!job) {
      return false;
    }

    return job.statusId == this.jobStatus_Cancelled;
  }

  isJobClosed(job: IJobScreen): boolean {
    if (!job) {
      return false;
    }

    return job.statusId == this.jobStatus_Cancelled || job.statusId == this.jobStatus_Closed;
  }

  isOrderCompletedOrCancelled(order: IOrderScreen) {
    return order && [OrderStatus.Closed, OrderStatus.Cancelled].some((s) => s == order.statusId);
  }

  saveMoDelPin() {
    this.isShownMoDelPin = false;
    this.taskService
      .updateMoDelPin({
        jobId: this.jobId,
        taskId: this.selectedTask.id,
        moDelPin: this.moDelPin,
      })
      .then((res) => {
        if (res) {
          this._selectedTask.moDelPin = this.moDelPin;
          this.reloadNotes(this.selectedTask.id);
        }
      });
  }

  saveReleaseCode() {
    this.isShownReleaseCode = false;
    this.taskService
      .updateReleaseCode({
        jobId: this.jobId,
        taskId: this.selectedTask.id,
        releaseCode: this.releaseCode,
      })
      .then((res) => {
        if (res) {
          this._selectedTask.releaseCode = this.releaseCode;
          this.reloadNotes(this.selectedTask.id);
        }
      });
  }

  showMoDelPinDialog() {
    this.moDelPin = this.selectedTask.moDelPin;
    this.isShownMoDelPin = true;
  }

  showReleaseCodeDialog() {
    this.releaseCode = this.selectedTask.releaseCode;
    this.isShownReleaseCode = true;
  }

  onEditSupplierScheduleClick() {
    this.editTaskScheduleVisible = true;
  }

  editLocations(taskId) {
    //this.showEditLocationsDialog = true;
    this.router.navigate(['/app/edit-task-location/' + taskId]);
  }

  editLocationsConfirm() {
    //this.showEditLocationsDialog = false;
    //..
  }

  getDriverChecklistPickUp(driverChecklist: ITaskStepNode[]) {
    var result: ITaskStepNode[] = [];
    for (let i = 0; i < driverChecklist.length; i++) {
      if (driverChecklist[i - 1]) {
        driverChecklist[i].minDate = driverChecklist[i - 1].date;
      } else {
        driverChecklist[i].minDate = null;
      }

      if (driverChecklist[i + 1]) {
        driverChecklist[i].maxDate = driverChecklist[i + 1].date;
      } else {
        driverChecklist[i].maxDate = null;
      }

      if (driverChecklist[i].isPickup == true) {
        result.push(driverChecklist[i]);
      }
    }
    return result;
  }

  /**
   * todo: side effects, what's min/max date being used for?
   * Should just get steps.filter(step => !step.isPickUp);
   * @param driverChecklist
   */
  getDriverChecklistDropOff(driverChecklist: ITaskStepNode[]) {
    var result: ITaskStepNode[] = [];
    for (let i = 0; i < driverChecklist.length; i++) {
      if (driverChecklist[i - 1]) {
        driverChecklist[i].minDate = driverChecklist[i - 1].date;
      } else {
        driverChecklist[i].minDate = null;
      }

      if (driverChecklist[i + 1]) {
        driverChecklist[i].maxDate = driverChecklist[i + 1].date;
      } else {
        driverChecklist[i].maxDate = null;
      }

      if (driverChecklist[i].isPickup !== true) {
        result.push(driverChecklist[i]);
      }
    }
    return result;
  }

  searchSuppliers() {
    this.router.navigate(['app/searchsupplier/' + this.selectedTask.id]);
  }

  impersonateUser(userId) {
    var obj = {
      ImpersonatedUserGUID: userId,
      ImpersonatedUserName: '',
      OriginalUserName: '',
      IsImpersonating: false,
    };
    this.userService.impersonateUser(obj).then((res) => {
      window.location.reload();
      window.location.href = '/';
    });
  }

  usersToSelectItems(l) {
    var users = [];
    l.forEach((o) => {
      users.push({
        label:
          o.firstName || o.lastName
            ? ((o.firstName ? o.firstName : '') + ' ' + (o.lastName ? o.lastName : '')).trim()
            : o.email,
        value: o.id,
      });
    });
    return users;
  }

  impersonateSupplier() {
    this.selectedImpersonateUser = null;

    this.orderService.getImpersonateSupplierUsers(this.jobId).then((res) => {
      this.impersonateSupplierUsers = this.usersToSelectItems(res);
    });

    this.showImpersonateSupplierDialog = true;
  }

  impersonateSupplierConfirm() {
    this.showImpersonateSupplierDialog = false;
    this.impersonateUser(this.selectedImpersonateUser);
  }

  impersonateCustomer() {
    this.selectedImpersonateUser = null;

    this.orderService.getImpersonateCustomerUsers(this.selectedOrder.id).then((res) => {
      this.impersonateCustomerUsers = this.usersToSelectItems(res);
    });

    this.showImpersonateCustomerDialog = true;
  }

  impersonateCustomerConfirm() {
    this.showImpersonateCustomerDialog = false;
    this.impersonateUser(this.selectedImpersonateUser);
  }

  isCancelOrderEnableForVME(): boolean {
    const isEngineius = this.organisationTypeId == OrganisationTypeEnum.Engineius;
    const isJobInProgress = ![JobStatus.Completed, JobStatus.Closed, JobStatus.Cancelled].includes(
      this.jobInfo.statusId,
    );

    return (isEngineius && isJobInProgress) || this.canCustomerCancelOrder();
  }

  canCustomerCancelOrder(): boolean {
    const isCustomer = this.organisationTypeId == OrganisationTypeEnum.Customer;

    if (!isCustomer || this.isJobCompletedOrClosed()) {
      return false;
    }

    return this.isCustomerAbleToCancelOrder;
  }

  private isJobCompletedOrClosed(): boolean {
    return [JobStatus.Completed, JobStatus.Closed, JobStatus.Cancelled].includes(
      this.jobInfo.statusId,
    );
  }

  isCancelJobEnableForVME() {
    return (
      // this.isCancelOrderEnableForVME() && this.jobInfo.supplierId != null // should be able to satisfy the conditions of cancelling an order before you can cancel a job
      this.isCancelOrderEnableForVME()
    );
  }

  canShowPriceDetails() {
    return (
      this.jobInfo != null &&
      (this.jobInfo.statusId == JobStatus.Closed ||
        this.jobInfo.statusId == JobStatus.Cancelled ||
        this.jobInfo.statusId == JobStatus.Paused ||
        this.jobInfo.statusId == JobStatus.Completed ||
        this.jobInfo.statusId == JobStatus.Started ||
        this.jobInfo.statusId == JobStatus.Cancelled)
    );
  }

  canDeploy() {
    return (
      this.jobInfo != null &&
      this.organisationTypeId == OrganisationTypeEnum.Engineius &&
      !this.jobInfo.supplierId
    );
  }

  @HostListener('document:click', ['$event'])
  documentClick(event: MouseEvent) {
    if (this.allowClosePopupPhoto) {
      this.isShownPopupPhoto = false;
      this.loadingPopupPhoto = false;
      this.allowClosePopupPhoto = false;
    }
  }

  openExpensePhoto(imagePublicUrl: string) {
    const extensions = imagePublicUrl.split('.');
    const extension = extensions[extensions.length - 1] === 'pdf' ? 'pdf' : 'jpg';
    this.expenseImages.push({
      url: imagePublicUrl,
      extension,
      big: imagePublicUrl,
      medium: imagePublicUrl,
      small: imagePublicUrl,
      key: imagePublicUrl,
    });
    this.allowClosePopupPhoto = false;
    setTimeout(() => {
      this.allowClosePopupPhoto = true;
    }, 200);
  }

  showPriceDetails() {
    var param: any = {
      jobId: this.jobId,
    };

    if (this.organisationTypeId == OrganisationTypeEnum.Engineius) {
      if (this.selectedOrder) {
        param.orderId = this.selectedOrder.id;
        param.customerId = this.selectedOrder.customerId;
      }

      if (this.jobInfo.supplierId) {
        param.supplierId = this.jobInfo.supplierId;
      }
    }
    if (this.organisationTypeId == OrganisationTypeEnum.Customer) {
      param = {
        orderId: this.selectedOrder.id,
      };
    }

    this.router.navigate(['app/pricedetails'], { queryParams: param });
  }

  // showPriceDetailsOrder() {
  //     var param: any = {
  //         orderId: this.selectedOrder.id
  //     };

  //     //used only for VME user
  //     if (this.organisationTypeId == 1) {
  //         param.customerId = this.selectedOrder.customerId;
  //     }
  //
  //     this.router.navigate(['app/pricedetails'], { queryParams: param });
  //}

  deletePhotoToTaskStep(image: IGalleryImage, stepType: StepType) {
    if (image) {
      this.confirmationService.confirm({
        message: `Are you sure want to delete selected image?`,
        accept: () => {
          this.taskService.deletePhotoToTaskStep(image).then((res) => {
            this.removeImageFromTaskSteps(this.selectedTask, image);

            if (stepType == StepType.PickupPhoto)
              this.updateImages(this.selectedTask, StepType.PickupPhoto, false);
            else if (stepType == StepType.DropOffPhoto)
              this.updateImages(this.selectedTask, StepType.DropOffPhoto, false);
          });
        },
      });
    }
  }

  removeImageFromTaskSteps(task: ITaskInfo, image: IGalleryImage) {
    if (image.isSignature) {
      var steps = task.completeTaskSteps.filter(
        (s) => StepType.PickupSignature == s.stepType || StepType.DropOffSignature == s.stepType,
      );
      steps.forEach((step) => {
        var photo = step.signatures.find((x) => x.id == image.id);

        if (photo) {
          var index = step.signatures.indexOf(photo, 0);
          if (index > -1) {
            step.signatures.splice(index, 1);
          }
        }
      });
    } else {
      var steps = task.completeTaskSteps.filter(
        (s) => StepType.PickupPhoto == s.stepType || StepType.DropOffPhoto == s.stepType,
      );

      steps.forEach((step) => {
        var photo = step.photos.find((x) => x.id == image.id);
        if (photo) {
          var index = step.photos.indexOf(photo, 0);
          if (index > -1) {
            step.photos.splice(index, 1);
          }
        }
      });
    }
  }

  showEditPhotoToTaskStep(image: IGalleryImage, type: StepType) {
    this.isShowEditPhotoToTaskStep = true;

    this.selectedImage = null;
    this.selectedImageSource = null;

    this.fileStorageService.getPreSignedURLs([image.source]).then((res) => {
      this.selectedImage = image;
      this.selectedImageSource = res.length ? res[0] : null;
      this.initAreaCondition(type);
    });

    this.addPhotoDescription = image.description;

    let steps = this.selectedTask.completeTaskSteps.filter((x) => x.stepType == type);
    if (steps.length) {
      let step = steps[0];
      this.selectedCompleteTaskStep = step;
      this.initAreas(this.selectedCompleteTaskStep);
      if (this.areas.length) {
        var item = this.areas.find((x) => x.value == image.area);
        this.selectedArea = item ? item.value : this.areas[0].value;
      }
    }
  }

  updatePhotoToStep() {
    this.isBusyAddPhotoToStep = true;

    var updatedImage = Object.assign({}, this.selectedImage);

    updatedImage.description = this.addPhotoDescription;
    updatedImage.photoArea = this.selectedArea;

    this.taskService
      .updatePhotoToTaskStep(updatedImage)
      .then((result) => {
        this.selectedImage.description = this.addPhotoDescription;
        this.selectedImage.area = this.selectedArea;
        this.selectedImage.areaTitle = this.getAreaTitle(this.selectedImage.area);

        var area = this.selectedCompleteTaskStep.areas.find(
          (x) => x.photoArea == this.selectedArea,
        );
        if (this.selectedArea == PhotoArea.FuelGaugeOdometer) {
          this.selectedImage.condition = PhotoAreaCondition.NotRequired;
        } else {
          this.selectedImage.condition = area ? area.condition : PhotoAreaCondition.Good;
        }

        //Need update task due do updateImages();
        this.updatePhotoOrSignatureData(this.selectedTask, this.selectedImage);

        this.updateImages(this.selectedTask, this.selectedCompleteTaskStep.stepType, false);

        this.isBusyAddPhotoToStep = false;
        this.isShowAddPhotoToTaskStep = false;
        this.isShowEditPhotoToTaskStep = false;
        this.messageService.add(
          Message.Success('Photo successfully updated.', 'Update Photo Data'),
        );
      })
      .catch((ex) => {
        this.isBusyAddPhotoToStep = false;
        this.messageService.add(
          Message.Error(
            'An error occurred while updating the photo. Try again.',
            'Update Photo Data',
          ),
        );
      });
  }

  updatePhotoOrSignatureData(task: ITaskInfo, image: IGalleryImage) {
    if (image.isSignature) {
      var steps = task.completeTaskSteps.filter(
        (s) => StepType.PickupSignature == s.stepType || StepType.DropOffSignature == s.stepType,
      );
      steps.forEach((step) => {
        var photo = step.signatures.find((x) => x.id == image.id);

        if (photo) {
          photo.photoArea = image.area;
          for (let i = 0; i < step.areas.length; i++) {
            if (step.areas[i].photoArea == image.area) {
              step.areas[i].condition = image.condition;
              break;
            }
          }
        }
      });
    } else {
      var steps = task.completeTaskSteps.filter(
        (s) => StepType.PickupPhoto == s.stepType || StepType.DropOffPhoto == s.stepType,
      );

      steps.forEach((step) => {
        var photo = step.photos.find((x) => x.id == image.id);
        if (photo) {
          photo.description = image.description;
          photo.photoArea = image.area;
          for (let i = 0; i < step.areas.length; i++) {
            if (step.areas[i].photoArea == image.area) {
              step.areas[i].condition = image.condition;
              break;
            }
          }
        }
      });
    }
  }

  public vehicle: IVehicle;
  public origVehicle: IVehicle;
  public isSearchVehicle: boolean = false;
  public showEditTaskDialog: boolean = false;
  public registrationNumber: string = '';
  public kerbWeight: number;
  public isShowWeightEditor: boolean = false;

  editTask(selectedTask) {
    this.vehicle = selectedTask.vehicle;
    this.registrationNumber = this.vehicle.registrationNumber;
    this.kerbWeight = this.vehicle.kerbWeight;
    this.isShowWeightEditor = this.kerbWeight <= 0;
    this.showEditTaskDialog = true;
  }

  taskUpdated(event) {
    this.LoadJob();
  }

  public oldKerbWeightEntry: number;

  limitKerbWeight(element) {
    var max_chars = 6;
    if (element.target.value === '' && element.key !== 'Backspace') {
      this.vehicle.kerbWeight = this.oldKerbWeightEntry;
    }
    this.oldKerbWeightEntry = this.vehicle.kerbWeight;

    if (element.target.value.length > max_chars) {
      this.vehicle.kerbWeight = element.target.value.substr(0, max_chars);
    }
  }

  getPrintName(isPickup: boolean) {
    var result = '';
    let step = this.selectedTask.completeTaskSteps.find(
      (x) => x.stepType === (isPickup ? StepType.PickupSignature : StepType.DropOffSignature),
    );
    if (step) {
      result = step.printName;
    }
    return result;
  }

  public showEditActualTaskTypeDialog: boolean = false;
  public actualTaskTypeId?: number;
  public taskTypes: SelectItem[] = [];
  public taskTypeId?: number;

  initEngineiusTaskTypes() {
    this.taskTypes = [];
    this.taskTypes.push({ label: 'Select task type', value: null });
    this.taskService.getActualTaskTypes().then((res) => {
      res.forEach((x) => this.taskTypes.push({ label: x.abbr, value: x.id }));
    });
  }

  confirmMultiOrderTaskType() {
    var hasDiffTaskOrderType = false;
    this.jobInfo.orders.forEach((order) => {
      var diffOrderTask = order.tasks.find(
        (t) => t.taskTypeId != this.taskTypeId && t.id != this.selectedTask.id,
      );
      if (diffOrderTask) hasDiffTaskOrderType = true;
    });

    if (this.jobInfo.orders.length > 1 && hasDiffTaskOrderType) {
      // notify of some order with mismatched task types
      this.confirmationService.confirm({
        message: `This job has different types of tasks. Please confirm that changing this task type will still allow the tasks within the job to be completed by an individual supplier. Do you want to continue?`,
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => {
          this.saveActualTaskType();
        },
        reject: () => {},
      });
    } else {
      this.saveActualTaskType();
    }
  }

  saveActualTaskType() {
    this.selectedTask.actualTaskTypeId = this.actualTaskTypeId;
    this.selectedTask.taskTypeId = this.taskTypeId;
    this.taskService
      .saveActualTaskType(this.selectedTask)
      .then(() => {
        var engType = this.taskTypes.find((x) => x.value == this.selectedTask.actualTaskTypeId);
        if (engType && this.selectedTask.actualTaskTypeId) {
          this.selectedTask.actualTaskTypeName = engType.label;
        }
        var cusType = this.taskTypes.find((x) => x.value == this.selectedTask.taskTypeId);
        if (cusType && this.selectedTask.taskTypeId) {
          this.selectedTask.taskTypeName = cusType.label;
        }

        this.showEditActualTaskTypeDialog = false;
        this.messageService.add(
          Message.Success('Engineius Movement Type updated', 'Engineius Movement Type'),
        );
        //update local
        this.jobInfo.orders.forEach((order, orderIndex) => {
          order.tasks.forEach((task, taskIndex) => {
            if (task.id == this.selectedTask.id) {
              this.jobInfo.orders[orderIndex].tasks[taskIndex].taskTypeId = this.taskTypeId;
              this.jobInfo.orders[orderIndex].tasks[taskIndex].actualTaskTypeId =
                this.actualTaskTypeId;
            }
          });
        });

        this.showEditActualTaskTypeDialog = false;
      })
      .catch((err) => {
        this.messageService.add(Message.Error(`Error: ${err.error}`, 'Task Type Update'));
      });
  }

  editActualTaskType(task: ITaskInfo) {
    this.logger.debug('Edit task: ', task);

    this.actualTaskTypeId = task.actualTaskTypeId;
    this.showEditActualTaskTypeDialog = true;
    this.taskTypeId = task.taskTypeId;
  }

  private initLateStatuses() {
    this.lateStatusesSelectItems = [{ value: 0, label: '' }];
    this.lateStatuses.forEach((x) =>
      this.lateStatusesSelectItems.push({ label: x.title, value: x.id }),
    );
  }

  private initFuelLevels() {
    this.fuelLevelsSelectItems = [{ value: 0, label: '' }];
    this.fuelLevelService.getFuelLevels().then((fuelLevels) => {
      fuelLevels.forEach((x) => {
        this.fuelLevelsSelectItems.push({ label: x.fuelLevelName, value: x.fuelLevel });
      });
    });
  }

  getFuelLevel(fuel: number) {
    const fuelLevel = this.fuelLevelsSelectItems.find((x) => x.value == fuel);
    return fuelLevel?.label;
  }

  getLateStatusTitle(id: number): string {
    let ls = this.lateStatuses.find((o) => o.id == id);
    return ls ? ls.title : null;
  }

  showLateStatusesDialog() {
    this.lateStatusesEdited = {
      taskId: this.selectedTask.id,
      pickupLateStatusId: this.selectedTask.pickupLateStatusId,
      pickupLateReason: this.selectedTask.pickupLateReason,
      dropoffLateStatusId: this.selectedTask.dropoffLateStatusId,
      dropoffLateReason: this.selectedTask.dropoffLateReason,
    };

    this.isShownLateStatusesDialog = true;
  }

  saveLateStatuses() {
    this.taskService
      .saveLateStatuses(this.lateStatusesEdited)
      .then(() => {
        this.selectedTask.pickupLateStatusId = this.lateStatusesEdited.pickupLateStatusId;
        this.selectedTask.pickupLateReason = this.lateStatusesEdited.pickupLateReason;
        this.selectedTask.dropoffLateStatusId = this.lateStatusesEdited.dropoffLateStatusId;
        this.selectedTask.dropoffLateReason = this.lateStatusesEdited.dropoffLateReason;

        this.isShownLateStatusesDialog = false;
        this.messageService.add(
          Message.Success('Late statuses successfully changed.', 'Change Late Statuses'),
        );
      })
      .catch((ex) => {
        this.messageService.add(
          Message.Error(
            'An error occurred while changing late statuses. Try again.',
            'Change Late Statuses',
          ),
        );
      });
  }

  showOdometerReadingDialog(isPickup: boolean) {
    this.odometerEdited = {
      taskId: this.selectedTask.id,
      isPickup: isPickup,
      odometer: isPickup ? this.selectedTask.pickupOdometer : this.selectedTask.dropoffOdometer,
    };

    this.isShownOdometerDialog = true;
  }

  saveOdometerReading() {
    this.taskService
      .saveOdometerReading(this.odometerEdited)
      .then(() => {
        if (this.odometerEdited.isPickup)
          this.selectedTask.pickupOdometer = this.odometerEdited.odometer;
        else this.selectedTask.dropoffOdometer = this.odometerEdited.odometer;

        this.isShownOdometerDialog = false;
        this.messageService.add(
          Message.Success('Odometer reading successfully changed.', 'Change Odometer Reading'),
        );
      })
      .catch((ex) => {
        this.messageService.add(
          Message.Error(
            'An error occurred while changing odometer reading. Try again.',
            'Change Odometer Reading',
          ),
        );
      });
  }

  get editedOdometerIsDropoffLessPickup(): boolean {
    return (
      this.selectedTask &&
      !this.odometerEdited.isPickup &&
      this.odometerEdited.odometer <= this.selectedTask.pickupOdometer
    );
  }

  get editedOdometerValid(): boolean {
    return this.odometerEdited.odometer > 0 && !this.editedOdometerIsDropoffLessPickup;
  }

  get lateReasonsValid(): boolean {
    var disable = false;

    if (
      !this.lateStatusesEdited.pickupLateStatusId == true &&
      !this.lateStatusesEdited.dropoffLateStatusId == true
    )
      disable = true;

    if (
      this.lateStatusesEdited.pickupLateStatusId > 0 &&
      !this.lateStatusesEdited.pickupLateReason == true
    )
      disable = true;

    if (
      this.lateStatusesEdited.dropoffLateStatusId > 0 &&
      !this.lateStatusesEdited.dropoffLateReason == true
    )
      disable = true;

    return disable;
  }

  saveFuelReading(isPickup) {
    let editedFuel: IFuelReading = {
      taskId: this.selectedTask.id,
      isPickup: isPickup,
      fuel: isPickup ? this.selectedTask.pickupFuel : this.selectedTask.dropoffFuel,
    };

    this.taskService
      .saveFuelReading(editedFuel)
      .then(() => {
        this.messageService.add(
          Message.Success('Fuel reading successfully changed.', 'Change Fuel Reading'),
        );
      })
      .catch((ex) => {
        this.messageService.add(
          Message.Error(
            'An error occurred while changing fuel reading. Try again.',
            'Change Fuel Reading',
          ),
        );
      });
  }

  // Recognition Statuses
  public showEditRegStatusDialog: boolean = false;
  public regStatusId?: number;
  public regStatuses: SelectItem[] = [];

  initRegStatuses() {
    this.regStatuses = [];
    this.regStatuses.push({ label: 'Select status...', value: null });
    this.taskService.getRegStatuses().then((res) => {
      res.forEach((x) => this.regStatuses.push({ label: x.name, value: x.id }));
    });
  }

  saveRegStatus() {
    this.selectedTask.regStatusId = this.regStatusId;
    const request: ISaveRegStatus = {
      taskId: this.selectedTask.id,
      jobId: this.selectedTask.jobId,
      regStatusId: this.selectedTask.regStatusId,
    };
    this.taskService.saveRegStatus(request).then((result) => {
      if (result) {
        var type = this.regStatuses.find((x) => x.value == this.selectedTask.regStatusId);
        if (type != null) {
          this.selectedTask.regStatusName = type.label;
        }

        this.showEditRegStatusDialog = false;
        this.messageService.add(Message.Success('Reg Status updated', 'Engineius Movement Type'));
      }
    });
  }

  editRegStatus(task: ITaskInfo) {
    this.regStatusId = task.regStatusId;
    this.showEditRegStatusDialog = true;
  }

  insuranceProviders: SelectItem[] = [];

  initInsuranceProviders(jobId) {
    this.insuranceProviders = [];
    this.insuranceProviderService.getProvidersByJobId(jobId).then((result) => {
      this.insuranceProviders.push({ label: 'Select provider...', value: null });
      result.forEach((item) =>
        this.insuranceProviders.push({ label: item.insuranceProvider, value: item.id }),
      );
    });
  }

  // Trade Plate Numbers
  public showEditTradePlateDialog: boolean = false;
  public tradePlateNumber?: string;
  private tradePlateRegExp = new RegExp(/^([a-zA-Z]{1}\d{4})$|^(\d{5})$|^(\d{3}[a-zA-Z]{1,3})$/);

  saveTradePlate() {
    var data = { jobId: this.jobInfo.id, tradePlateNumber: this.tradePlateNumber };
    this.jobService
      .saveTradePlate(data)
      .then((result) => {
        if (result) {
          this.showEditTradePlateDialog = false;
          this.jobInfo.tradePlateNumber = this.tradePlateNumber;
          this.messageService.add(Message.Success('Trade Plate updated', 'Trade Plate'));
        }
      })
      .catch((res) => {
        this.logger.error('Error Saving Trade Plate', res);
        this.messageService.add(Message.Error(res.error.Message, 'Trade Plate'));
      });
  }

  editTradePlate() {
    this.tradePlateNumber = this.jobInfo.tradePlateNumber;
    this.showEditTradePlateDialog = true;
  }

  isTradePlateValid(text: string) {
    return text && text.match(this.tradePlateRegExp);
  }

  deployJob() {
    this.router.navigate(['app/job-deployment', this.jobId]);
  }

  markPriority() {
    this.vmeInboxjobsService.markPriorityDeployment([this.jobId]).then(() => {
      this.jobInfo.priorityDeployment = !this.jobInfo.priorityDeployment;
    });
  }

  hasInvoice() {
    this.jobService.isInvoiced(this.jobId).then((res) => {
      this.isInvoiced = res;
    });
  }

  hasCustomerInvoice() {
    this.jobService.isCustomerInvoiced(this.jobId).then((res) => {
      this.isCustomerInvoiced = res;
    });
  }

  hasSupplierInvoice() {
    this.jobService.isSupplierInvoiced(this.jobId).then((res) => {
      this.isSupplierInvoiced = res;
    });
  }

  onPauseResumeTask(pauseStatus: boolean) {
    this.pauseReasonId = null;
    this.logger.debug('Pause Status: ', pauseStatus);
    if (!pauseStatus) {
      this.showPauseReasonDialog = true;
    } else {
      this.pauseResumeTask();
    }
  }

  pauseResumeTask() {
    //get the last task step

    let pauseTask: IPauseTask = {
      id: 0,
      isResume: this.isJobPaused,
      pauseReasonId: this.pauseReasonId,
      createdOn: moment(moment().format('YYYY-MM-DD HH:mm:ss')).toDate(),
      latitude: null,
      longitude: null,
    };

    //create a pause reason and a pause object
    if (pauseTask.isResume) {
      this.taskService
        .resumeTask(this.jobId, this._selectedTask.id, pauseTask)
        .then((res) => {
          this.isJobPaused = false;
          this.LoadJob();
        })
        .catch((err) => {});
    } else {
      this.taskService
        .pauseTask(this.jobId, this._selectedTask.id, pauseTask)
        .then((res) => {
          this.isJobPaused = true;
          this.showPauseReasonDialog = false;
          this.LoadJob();
        })
        .catch((err) => {
          this.showPauseReasonDialog = false;
        });
    }
  }

  validPhoneNumber(phoneNumber: string) {
    return phoneNumber.match(
      /^(((\+44\s?\d{4}|\(?0\d{4}\)?)\s?\d{3}\s?\d{3})|((\+44\s?\d{3}|\(?0\d{3}\)?)\s?\d{3}\s?\d{4})|((\+44\s?\d{2}|\(?0\d{2}\)?)\s?\d{4}\s?\d{4}))(\s?\#(\d{4}|\d{3}))?$/,
    );
  }

  validMobileNumber(phoneNumber: string) {
    return phoneNumber.match(
      /^(((\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3})|((\+44\s?7\d{2}|\(?07\d{2}\)?)\s?\d{3}\s?\d{4})|((\+44\s?7\d{1}|\(?07\d{1}\)?)\s?\d{4}\s?\d{4}))?$/,
    );
  }

  isJobHeld = () => moment(this.jobInfo.heldEndDate).isAfter(moment());
  stepCalendar; // set to current step being edited to allow calendar entry
  public showSupplierPremiums = false;
  public showCustomerPremiums = false;

  public previewPhoto: string;
  public showPreviewChecklistAnswerPhoto: any = false;
  public showPreviewChecklistPhoto: any = false;
  public showRemoveConfirm: any;
  public unassignReasons: any[];
  public unassignComment: string;
  public unassignReasonId: any;
  public organisationTypeEnum = OrganisationTypeEnum;
  showPreviousSuppliers: boolean = false;
  showBookingInfo = false;

  releaseFromHold() {
    //set held end date back by 1 sec from today
    this.heldEndDate = this.exisingCapacityEndDate = moment(
      moment().subtract(1, 's').format('YYYY-MM-DD HH:mm:ss'),
    ).toDate();
    this.exisingCapacityEndDate = moment(moment().format('YYYY-MM-DD HH:mm:ss')).toDate();

    this.scheduledDate = moment(this.jobInfo.scheduledDate).toDate();

    (this.buyItNowPrice = this.jobInfo.buyItNowPrice),
      (this.existingCapacityPrice = this.jobInfo.existingCapacityPrice),
      (this.supplierPrice = this.jobInfo.supplierPrice),
      (this.insuranceCost = this.jobInfo.insuranceCost),
      (this.insuranceProviderId = this.jobInfo.insuranceProviderId);
    this.saveChangeJobDetails();
  }

  toggleStepDetail(step: ITaskStepNode) {
    step.showDetails = !step.showDetails;
  }

  handleShowStepPhotos(stepPhotos: IPhotoOrganisationTaskStep[]) {
    this.selectedStepPhotos = [];
    if (stepPhotos) {
      this.selectedStepPhotos = stepPhotos.reduce(
        (mediaPaths: string[], val: IPhotoOrganisationTaskStep, index) => {
          mediaPaths.push(val.photoLocation);
          return mediaPaths;
        },
        [],
      );
      this.showStepPhotosDialog = true;
    }
  }

  handleShowStepPhoto(stepPhoto: string) {
    if (stepPhoto) {
      this.selectedStepPhotos = [stepPhoto];
      this.showStepPhotosDialog = true;
    }
  }

  handleShowCurrentLocation(step: ITaskStepNode) {
    if (step) {
      this.selectedStep = step;
      this.currentLocation = new MarkerPoint();
      this.currentLocation.longitude = this.selectedStep.longitude;
      this.currentLocation.latitude = this.selectedStep.latitude;
      this.showCurrentLocationDialog = true;
    }
  }

  checkboxChange(
    event: Event,
    answerId: number,
    taskorganisationSpecificStep: ITaskOrganisationSpecificSteps,
  ) {
    const stepId = taskorganisationSpecificStep.id;

    // Deselect any chosen items if we're not allowing multichoice
    if (!taskorganisationSpecificStep.organisationSpecificStep.isMultiChoiceSelection) {
      taskorganisationSpecificStep.answers
        .filter((x) => x.id != answerId)
        .map((x) => {
          x.isSelected = false;
          x.responseTime = new Date();
        });
    }

    taskorganisationSpecificStep.answers
      .filter((x) => x.id == answerId)
      .map((x) => {
        x.isSelected = this.editAnswers[stepId][x.id]['isSelected'];
        x.responseTime = new Date();
      });

    taskorganisationSpecificStep.answers.forEach((x) => {
      this.editAnswers[taskorganisationSpecificStep.id][x.id]['isSelected'] = x.isSelected;
    });

    this.saveEdit(taskorganisationSpecificStep);
  }

  saveEdit(taskorganisationSpecificStep) {
    let x: ISyncOrganisationStepDTO = {
      jobId: this.selectedTask.jobId,
      taskId: this.selectedTask.id,
      orgStepId: taskorganisationSpecificStep.id,
      completedDateTime: new Date().toISOString(),
      customStepPhotos: taskorganisationSpecificStep.customStepPhotos,
      answers: taskorganisationSpecificStep.answers,
      comment: taskorganisationSpecificStep.comment,
    };

    return this.taskService.UpdateOrgStepTaskAnswers(
      this.selectedTask.id,
      taskorganisationSpecificStep.organisationSpecificStepId,
      x,
    );
  }

  checklistManualEdit() {
    this.isDriverChecklistManualEdit = !this.isDriverChecklistManualEdit;

    if (!this.isDriverChecklistManualEdit) {
      return;
    }

    this.editAnswers = {};

    this.refreshEditAnswers(this.driverChecklistPickUp);
    this.refreshEditAnswers(this.driverChecklistDropOff);
  }

  refreshEditAnswers(checklist) {
    checklist.forEach((x) => {
      if (x.taskOrganisationSpecificStep == null) {
        return;
      }

      const taskOrgStepId = x.taskOrganisationSpecificStep.id;

      if (!this.editAnswers[taskOrgStepId]) {
        this.editAnswers[taskOrgStepId] = {
          comment: x.taskOrganisationSpecificStep.comment,
        };

        x.taskOrganisationSpecificStep.answers.forEach((x) => {
          this.editAnswers[taskOrgStepId][x.id] = {
            isSelected: x.isSelected,
          };
        });
      }
    });
  }

  commentChange(event: Event, taskorganisationSpecificStep: ITaskOrganisationSpecificSteps) {
    // @ts-ignore
    taskorganisationSpecificStep.comment = event.target.value;

    return this.saveEdit(taskorganisationSpecificStep);
  }

  initChecklistStepPhotosDialog(taskorganisationSpecificStep: ITaskOrganisationSpecificSteps) {
    this.currentTaskStep = taskorganisationSpecificStep;
    this.selectedImage = null;
    this.showChecklistStepPhotosDialog = true;
  }

  initChecklistStepAnswerPhotosDialog(
    taskOrganisationSpecificStep: ITaskOrganisationSpecificSteps,
    taskStepAnswer: ITaskStepAnswer,
  ) {
    this.currentTaskStepAnswer = taskStepAnswer;
    this.selectedImage = null;
    this.currentTaskStep = taskOrganisationSpecificStep;
    this.showChecklistStepAnswerPhotosDialog = true;
  }

  previewChecklistAnswerPhoto(
    am: IPhotoOrganisationTaskStep,
    taskStep: ITaskOrganisationSpecificSteps,
  ) {
    this.selectedImage = am;
    this.previewTaskStep = taskStep;
    this.showPreviewChecklistAnswerPhoto = true;
  }

  previewChecklistPhoto(am: IPhotoOrganisationTaskStep, taskStep: ITaskOrganisationSpecificSteps) {
    this.selectedImage = am;
    this.previewTaskStep = taskStep;
    this.showPreviewChecklistPhoto = true;
  }

  deleteChecklistAnswerPhoto(selectedImage: any) {
    // Hide the dialog
    this.showPreviewChecklistAnswerPhoto = false;

    // Remove it from the display
    this.previewTaskStep.answers.forEach((x) => {
      x.answerMedia = x.answerMedia.filter((x) => x.mediaId != selectedImage.mediaId);
    });

    // Remove it from the database
    return this.taskService.deleteChecklistPhoto({
      mediaId: selectedImage.mediaId,
      jobId: this.selectedTask.jobId,
      taskid: this.selectedTask.id,
      objectKey: selectedImage.objectKey,
      isAnswerPhoto: true,
    });
  }

  deleteChecklistPhoto(selectedImage: any) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this checklist photo?',
      header: 'Delete Photo?',
      acceptLabel: 'Yes',
      rejectLabel: 'No',
      accept: () => {
        this.showPreviewChecklistPhoto = false;

        // Remove it from the display
        this.previewTaskStep.stepPhotos = this.previewTaskStep.stepPhotos.filter(
          (x) => x.mediaId != this.selectedImage.mediaId,
        );

        return this.taskService.deleteChecklistPhoto({
          mediaId: selectedImage.mediaId,
          jobId: this.selectedTask.jobId,
          taskid: this.selectedTask.id,
          objectKey: selectedImage.objectKey,
          isAnswerPhoto: false,
        });
      },
    });
  }

  taskStepCheckChanged(step: any) {
    if (step.isComplete) {
      this.stepCalendar = step;
    } else {
      // uncomplete the step
      step.date = null;
      this.onDriverChecklistChanged(step);

      if (step == this.stepCalendar) {
        this.stepCalendar = null;
      }
    }
  }

  previousAssignments(jobInfo: IJobScreen): IJobAssignment[] {
    if (jobInfo != null && jobInfo.jobAssignments != null) {
      return jobInfo.jobAssignments.filter((x) => x.unassigned == true);
    }
  }

  logCall() {
    let exTasks: ILogCallTaskInfo[] = [];
    this.jobInfo.orders.forEach((ord) => {
      let ordTasks = ord.tasks.map((tsk) => {
        return {
          id: tsk.id,
          jobId: tsk.jobId,
          registrationNumber: tsk.vehicle.registrationNumber,
        };
      });

      exTasks.push(...ordTasks);
    });

    let dto = <ILogCallJobInfo>{
      id: this.jobId,
      jobNumber: this.jobInfo.jobNumber,
      statusText: this.getJobStatusText(),
      tasks: exTasks,
      isAssociatedThisCall: true,
    };

    this.eventService.InternalTelephonyEvent.emit({
      eventType: TelephonyEventEnum.LogCall,
      telephonyJobInfo: dto,
    });

    this.jobAssociatedWithCall = true;
  }

  getOrderStatusText(statusId: number) {
    switch (statusId) {
      case 1:
        return 'Completed';
      case 2:
        return 'Invoiced';
      case 3:
        return 'Closed';
      case 4:
        return 'Cancelled';
      case 5:
        return 'Created';
      case 6:
        return 'Started';
      default:
        return 'Unknown';
    }
  }

  getJobStatusText() {
    switch (this.jobInfo.statusId) {
      case this.jobStatus_Closed:
        return 'Closed';
      case this.jobStatus_Started:
        return 'Started';
      case this.jobStatus_Created:
        return 'Created';
      case this.jobStatus_Completed:
        return 'Completed';
      case this.jobStatus_Cancelled:
        return 'Cancelled';
      case this.jobStatus_Paused:
        return 'Paused';
      default:
        return 'Unknown';
    }
  }

  disassociateJobFromCall() {
    this.eventService.DisassociateJobFromCall.emit(this.jobId);
    this.jobAssociatedWithCall = false;
  }

  deployJobToSupplier() {
    this.eventService.OrganisationSelectorEvent.emit({
      eventType: OrganisationSelectorEventTypeEnum.OpenSelector,
      args: true,
      forDeployment: true,
    });
  }

  onAllocateJobSaved() {
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    this.LoadJob();
  }

  onScheduleUpdated() {
    this.LoadJob();
  }

  scrollTo(divId: string) {
    let x = document.getElementById(divId);
    x.scrollIntoView();
  }

  setRegStatusToManualUpdate(selectedTask: ITaskInfo) {
    this.regStatusId = RegStatusEnum.ManuallyUpdated;
    this.saveRegStatus();
  }

  customerReschedule() {}

  jobDeploymentUrl(jobId: number) {
    return '/app/job-deployment/' + jobId.toString();
  }

  showOdometerPhoto(image: any) {
    this.showOdometerImage = true;
    this.odometerImageSource = null;

    this.fileStorageService.getPreSignedURLs([image.source]).then((res) => {
      this.odometerImageSource = res.length ? res[0] : null;
    });
  }

  odometerDialogVisibleChanged(visible: boolean) {
    this.showOdometerImage = visible;
  }
  onClosePhoto() {
    this.scrollTo('additional-cost');
  }

  onExpenseImagePreviewClose() {
    this.expenseImages = [];
  }

  hasClassInElementOrParents(element: HTMLElement | null, className: string): boolean {
    if (!element) {
      return false;
    }
    if (element.classList.contains(className)) {
      return true;
    }
    return this.hasClassInElementOrParents(element.parentElement, className);
  }

  /**
   * Formulas
   * 100 x (A - B) / (A+B)/2
   */
  get differencePercent(): number {
    if (!this.selectTask) return 0;

    const actualMiles = this.selectedTask.actualMiles;
    const estimatedMiles = this.selectedTask.estimatedMiles;

    const calculatedPart1 = estimatedMiles - actualMiles;
    const calculatedPart2 = (estimatedMiles + actualMiles) / 2;
    const result = (calculatedPart1 / calculatedPart2) * 100;
    return Math.abs(result);
  }

  get displayRedAlert(): string {
    return this.differencePercent > 15 ? 'drop-off-color' : '';
  }

  showEditSchedule() {
    this.editTaskScheduleVisible = true;
  }

  protected readonly BannerDisplayWeekEnum = BannerDisplayWeekEnum;

  onTaskSiteOpeningHoursUpdated(value: ITaskSiteOpeningHoursDisplay) {
    this.taskSiteOpeningHours = value;
  }

  showBookingInfoDialog(order, event: MouseEvent) {
    //Stop the click on the info icon from also triggering a click event on the order row
    event.stopPropagation();

    this.currentOrder = order;
    this.showBookingInfo = true;
  }

  getStatusName(statusName: string) {
    if (statusName === 'Internal Request' || statusName === 'Internal Updated') return 'Pending';
    return statusName;
  }

  private getDirectedSuppliers() {
    if (this.organisationTypeId != OrganisationTypeEnum.Engineius) return;
    this.directedSuppliers = [];
    this.supplierService.getDirectedSuppliers(this.jobId).then((res) => {
      res.forEach((item) => {
        this.directedSuppliers.push({
          label: item.supplier.name,
          value: item.supplier.id,
          tag: item,
        });
      });
    });
  }

  loadPhotoAreas(): void {
    this.photoAreaService.getAll().then((areas) => {
      this.photoAreas = areas;
    });
  }

  jobHasOrdersWithInspectAndCollect(): boolean {
    return this.jobInfo?.orders?.some((order) =>
      [TaskType.DrivenPlatedInspection, TaskType.DrivenInsuredInspection].includes(
        order.taskTypeId,
      ),
    );
  }

  async getInspectionHandoverDetails(taskId: number) {
    this.inspectionHandoverLoading = true;
    return this.taskService
      .getForComponent(taskId, 'job-screen-inspection-handover')
      .then((handover: JobScreenInspectionHandover) => {
        this.inspectionHandover = handover;
      })
      .finally(() => {
        this.inspectionHandoverLoading = false;
      });
  }

  viewInspectionHandoverMedia(media: JobScreenInspectionHandoverMedia): void {
    this.selectedMedia = media;
    this.isViewInspectionRecordingVisible = true;
  }

  editTaskSequence(): void {
    this.taskSequenceEditorVisible = true;
  }

  taskSequenceUpdated(): void {
    this.taskSequenceEditorVisible = false;
    this.eventService.RefreshJobEvent.emit(RefreshJobEventTypeEnum.RefreshHistory);
    this.LoadJob();
  }

  asCustomerCharge(itemUntyped: any): ICustomerAdditionalCharge {
    return itemUntyped as ICustomerAdditionalCharge;
  }

  protected hasCustomerBookingRole: boolean = false;
  protected hasCustomerPriceRole: boolean = false;
  private async initRoles() {
    this.hasCustomerBookingRole = await this.userService.isUserInRole('CUSTOMER_BOOKING');
    this.hasCustomerPriceRole = await this.userService.isUserInRole('CUSTOMER_PRICE');
  }
}
